import React from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { NorthIndianSvgChart, SouthIndianSvgChart, EastIndianSvgChart } from './ChartViewer';
import { SudarshanChakraSvg } from './SudarshanChakraSvg';
import { getPlanetFullName, ZODIAC_HI } from '../utils/language';
import { formatDOB } from '../utils/dateUtils';
import { AstrologerBranding, getSavedAstrologerBranding } from '../lib/astrologerBrandingService';
import {
  VISHNU_SAHASRANAMA_INVOCATIONS,
  TWELVE_BHAVA_READINGS,
  LAL_KITAB_DEBTS,
  TWENTY_YEAR_FORECASTS,
  generateAstrographScores
} from '../data/detailedReportData';
import {
  calculateIshtaKaal,
  getAvakahadaDetails,
  getGhatChakraDetails,
  calculateNumerologyProfile
} from '../utils/bhriguPatrikaCalculations';
import { PaperSize } from '../utils/pdfExportUtil';
import {
  getActive5LevelDashaPath,
  calculatePratyantardashas,
  calculateSookshmaDashas,
  calculatePranaDashas,
  PratyantarDashaItem,
  SubDashaItem
} from '../utils/dashaCalculator';
import { NAKSHATRAS, ZODIAC_SIGNS, getSavBySign } from '../utils/vedicEngine';
import { GranthaPages } from './granthas/GranthaPages';

export type DossierScope =
  | 'GRAND'
  | 'GRANTHA_1'
  | 'GRANTHA_2'
  | 'GRANTHA_3'
  | 'GRANTHA_4'
  | 'GRANTHA_5'
  | 'GRANTHA_6'
  | 'GRANTHA_7'
  | 'GRANTHA_8'
  | 'BRIHAT'
  | 'CAREER'
  | 'MARRIAGE'
  | 'REMEDIAL'
  | 'SUMMARY';
export type PrintTheme = 'deluxe' | 'eco-bw';

export interface BrihatJanmapatrikaReportProps {
  kundali: CompleteKundali;
  lang?: Language;
  branding?: AstrologerBranding;
  chartStyle?: ChartStyle;
  reportScope?: DossierScope;
  paperSize?: PaperSize;
  printTheme?: PrintTheme;
  activeGrantha?: number | 'ALL';
}

export const BrihatJanmapatrikaReport: React.FC<BrihatJanmapatrikaReportProps> = ({
  kundali,
  lang = 'hi',
  branding: propBranding,
  chartStyle = 'north',
  reportScope = 'BRIHAT',
  paperSize = 'a4',
  printTheme = 'deluxe',
  activeGrantha = 'ALL'
}) => {
  const activeBranding = propBranding || getSavedAstrologerBranding();
  const activeLang = lang || 'hi';
  const isHi = activeLang === 'hi' || activeLang === 'sa';
  const isBw = printTheme === 'eco-bw';

  // Responsive page sizing tokens based on selected paper size (Auto-resizes to eliminate clipping)
  const pagePaperSizeClass = paperSize === 'letter'
    ? 'min-h-[1056px] max-w-[816px]'
    : paperSize === 'legal'
    ? 'min-h-[1344px] max-w-[816px]'
    : paperSize === 'a3'
    ? 'min-h-[1587px] max-w-[1123px]'
    : 'min-h-[1123px] max-w-[794px]'; // A4 standard

  // Theme styling tokens
  const containerBg = isBw ? 'bg-white text-black' : 'bg-[#FFFDF7] text-[#263332]';
  const pageBorder = isBw ? 'border-2 border-black' : 'border-4 border-[#174C4B]';
  const innerCardBg = isBw ? 'bg-white border border-black shadow-none' : 'bg-[#F7F3EA]/75 border border-[#E5DDD0] shadow-2xs';
  const tableHeaderBg = isBw ? 'bg-gray-100 text-black border-b border-black' : 'bg-[#E8F0EF] text-[#174C4B] border-b border-[#E5DDD0]';
  const tableBorder = isBw ? 'border border-black' : 'border border-[#E5DDD0]';
  const tableRowEven = isBw ? 'bg-white' : 'bg-[#FFFDF7]';
  const tableRowOdd = isBw ? 'bg-gray-50/50' : 'bg-[#F7F3EA]/60';
  const headingPrimary = isBw ? 'text-black' : 'text-[#174C4B]';
  const headingAccent = isBw ? 'text-black' : 'text-[#B58A4A]';
  const mutedText = isBw ? 'text-gray-700' : 'text-[#4B5956]';
  const runningHeaderBorder = isBw ? 'border-b border-black text-black' : 'border-b border-[#E5DDD0] text-[#4B5956]';

  const renderPageFooter = (pageStr?: string) => {
    const parts: string[] = [];
    if (activeBranding.sansthan) parts.push(activeBranding.sansthan);
    if (activeBranding.name) parts.push(activeBranding.name);
    if (activeBranding.phone) parts.push(`(${activeBranding.phone})`);

    const content = parts.length > 0
      ? parts.join(' • ')
      : (isHi ? 'वेदांग ज्योतिषम् • वृहत् वैदिक जन्मपत्रिका' : 'Vedang Jyotisham • Classical Vedic Horoscope Compendium');

    return (
      <div className={`pt-3 mt-auto border-t text-center text-[10px] flex items-center justify-between ${isBw ? 'border-black text-black' : 'border-[#E5DDD0] text-[#7B8782]'}`}>
        <span className="font-serif">{content}</span>
        {pageStr && <span className="font-mono font-bold">{pageStr}</span>}
      </div>
    );
  };

  const {
    birthDetails,
    planets = [],
    houses = [],
    charts: rawCharts = {},
    avasthas,
    gemstones
  } = kundali;
  const charts = (rawCharts || {}) as Record<string, any>;

  const rawAshtakvarga = kundali.ashtakvarga || (kundali as any).ashtakavarga;
  const ashtakvargaData = rawAshtakvarga || {
    sarvashtakvarga: [28, 32, 29, 34, 27, 26, 31, 24, 30, 33, 31, 26],
    bhinna: {} as any
  };

  const active5Dasha = React.useMemo(() => getActive5LevelDashaPath(kundali.dashas || []), [kundali.dashas]);

  const vimshottariList = React.useMemo(() => {
    if (kundali.dashas && kundali.dashas.length > 0) {
      return kundali.dashas.map((d) => {
        const sYear = new Date(d.startDate).getFullYear();
        const eYear = new Date(d.endDate).getFullYear();
        const now = Date.now();
        const isActive = d.isCurrent || (now >= new Date(d.startDate).getTime() && now <= new Date(d.endDate).getTime());
        return {
          planet: d.planet,
          years: `${d.durationYears || Math.max(1, Math.round((new Date(d.endDate).getTime() - new Date(d.startDate).getTime()) / (365.25 * 86400000)))}y`,
          dates: `${sYear}–${eYear}`,
          active: Boolean(isActive)
        };
      });
    }
    return [];
  }, [kundali.dashas]);

  const activePratyantardashas: PratyantarDashaItem[] = React.useMemo(() => {
    if (!active5Dasha?.ad) return [];
    return calculatePratyantardashas(active5Dasha.ad.planet, active5Dasha.ad.startDate, active5Dasha.ad.endDate);
  }, [active5Dasha?.ad]);

  const activeSookshmaDashas: SubDashaItem[] = React.useMemo(() => {
    if (!active5Dasha?.pd) return [];
    return calculateSookshmaDashas(active5Dasha.pd.planet, active5Dasha.pd.startDate, active5Dasha.pd.endDate);
  }, [active5Dasha?.pd]);

  const activePranaDashas: SubDashaItem[] = React.useMemo(() => {
    if (!active5Dasha?.sd) return [];
    return calculatePranaDashas(active5Dasha.sd.planet, active5Dasha.sd.startDate, active5Dasha.sd.endDate);
  }, [active5Dasha?.sd]);

  const moonPlanet = planets.find((p) => p.name === 'Moon');
  const janmaNakName = moonPlanet?.nakshatra || 'Ashwini';
  const rawJanmaIdx = NAKSHATRAS.findIndex(
    n => n.name.toLowerCase() === janmaNakName.toLowerCase() ||
         janmaNakName.toLowerCase().startsWith(n.name.toLowerCase().slice(0, 4)) ||
         n.name.toLowerCase().startsWith(janmaNakName.toLowerCase().slice(0, 4))
  );
  const janmaNakIdx = rawJanmaIdx >= 0 ? rawJanmaIdx : 0;
  const sunPlanet = planets.find((p) => p.name === 'Sun');
  const ascPlanet = planets.find((p) => (p.name as string) === 'Ascendant' || (p.name as string) === 'Lagna');
  const ascendant = ascPlanet ? {
    sign: ascPlanet.sign,
    longitude: ascPlanet.longitude,
    degreeFormatted: ascPlanet.degreeFormatted,
    nakshatra: ascPlanet.nakshatra,
    pada: ascPlanet.pada
  } : {
    sign: houses?.[0]?.sign || 'Aries',
    longitude: houses?.[0]?.degreeStart || houses?.[0]?.bhavaMadhya || 0,
    degreeFormatted: houses?.[0]?.degreeFormatted || '00°00\'00"',
    nakshatra: '—',
    pada: 1
  };

  // Classical Calculations
  const ishtaKaal = (birthDetails?.date && birthDetails?.time)
    ? calculateIshtaKaal(
        birthDetails.date,
        birthDetails.time,
        birthDetails.latitude ?? 28.61,
        birthDetails.longitude ?? 77.23
      )
    : {
        ishtaGhati: 0,
        ishtaPala: 0,
        ishtaVipala: 0,
        ishtaKaalFormatted: '—',
        dinmaanGhati: '—',
        ratrimaanGhati: '—',
        sthanikSanskar: '—',
        sthanikSamaya: '—',
        velantar: '—',
        samaptikKaal: '—',
        sunriseFormatted: '—',
        sunsetFormatted: '—',
        suryaRashiDegree: '—',
        lagnaDegree: '—',
        ayana: 'Uttarayan' as const,
        ritu: '—',
        samvatVikram: 2081,
        samvatSaka: 1946,
        bhayatFormatted: '—',
        bhabhogFormatted: '—',
        bhayatBhabhogProgress: 0,
        birthBhogyaDasha: '—',
        sunriseTithi: '—',
        birthTithi: '—',
        tithiEndTime: '—',
        sunriseNakshatra: '—',
        birthNakshatra: '—',
        nakshatraEndTime: '—'
      };

  const avakahadaData = getAvakahadaDetails(moonPlanet?.nakshatra || 'Ashwini', moonPlanet?.pada || 1);
  const ghatChakraData = getGhatChakraDetails(moonPlanet?.sign || 'Aries');
  const numerology = calculateNumerologyProfile(birthDetails?.date);

  // Gemstones
  const lifeStone = Array.isArray(gemstones) ? (gemstones.find((g) => g.type?.includes('Life')) || gemstones[0]) : null;
  const luckyStone = Array.isArray(gemstones) ? (gemstones.find((g) => g.type?.includes('Lucky')) || gemstones[1]) : null;
  const fortuneStone = Array.isArray(gemstones) ? (gemstones.find((g) => g.type?.includes('Fortune')) || gemstones[2]) : null;

  // Render a large prominent chart with exact styling and optional SAV overlay
  const renderLargeChart = (chartData: any, customPlanets?: any[], showSav: boolean = false) => {
    if (!chartData) return null;
    const chartPlanets = customPlanets || chartData.planets || planets || [];
    const chartAshtakvarga = showSav ? ashtakvargaData : undefined;
    if (chartStyle === 'south') {
      return (
        <div className="w-full aspect-square max-w-[380px] mx-auto">
          <SouthIndianSvgChart chartData={chartData} planets={chartPlanets} ashtakvarga={chartAshtakvarga} lang={activeLang} />
        </div>
      );
    }
    if (chartStyle === 'east') {
      return (
        <div className="w-full aspect-square max-w-[380px] mx-auto">
          <EastIndianSvgChart chartData={chartData} planets={chartPlanets} lang={activeLang} />
        </div>
      );
    }
    return (
      <div className="w-full aspect-square max-w-[380px] mx-auto">
        <NorthIndianSvgChart
          chartData={chartData}
          planets={chartPlanets}
          ashtakvarga={chartAshtakvarga}
          lang={activeLang}
          showDegrees={true}
          antiOverlap={true}
        />
      </div>
    );
  };

  // Render a standard side-by-side grid chart
  const renderVargaChart = (chartData: any, customPlanets?: any[]) => {
    if (!chartData) return null;
    const chartPlanets = customPlanets || chartData.planets || planets || [];
    if (chartStyle === 'south') {
      return (
        <div className="w-full aspect-square max-w-[330px] mx-auto">
          <SouthIndianSvgChart chartData={chartData} planets={chartPlanets} lang={activeLang} />
        </div>
      );
    }
    if (chartStyle === 'east') {
      return (
        <div className="w-full aspect-square max-w-[330px] mx-auto">
          <EastIndianSvgChart chartData={chartData} planets={chartPlanets} lang={activeLang} />
        </div>
      );
    }
    return (
      <div className="w-full aspect-square max-w-[330px] mx-auto">
        <NorthIndianSvgChart chartData={chartData} planets={chartPlanets} lang={activeLang} />
      </div>
    );
  };

  const vishnuBrief = VISHNU_SAHASRANAMA_INVOCATIONS[0].brief[activeLang] || VISHNU_SAHASRANAMA_INVOCATIONS[0].brief['hi'] || VISHNU_SAHASRANAMA_INVOCATIONS[0].brief['en'];
  const astrographCurrent = generateAstrographScores(2025);

  // 16 Divisional charts catalog
  const shodaVargas = [
    { code: 'D1', name: isHi ? 'लग्न कुण्डली (D1)' : 'Lagna Chart (D1)', desc: isHi ? 'देह विचार (Physical Self & Vitality)' : 'Physical Self & Constitution', data: charts.D1 },
    { code: 'D2', name: isHi ? 'होरा कुण्डली (D2)' : 'Hora Chart (D2)', desc: isHi ? 'सम्पदा विचार (Wealth & Reserves)' : 'Liquid Wealth & Reserves', data: charts.D2 || charts.D1 },
    { code: 'D3', name: isHi ? 'द्रेष्काण कुण्डली (D3)' : 'Drekkana Chart (D3)', desc: isHi ? 'भ्रातृसौख्यम् (Siblings & Enterprise)' : 'Siblings & Courage', data: charts.D3 || charts.D1 },
    { code: 'D4', name: isHi ? 'चतुर्थांश कुण्डली (D4)' : 'Chaturthamsha (D4)', desc: isHi ? 'भाग्य व अचल सम्पत्ति' : 'Fixed Assets & Net Worth', data: charts.D4 || charts.D1 },
    { code: 'D7', name: isHi ? 'सप्तमांश कुण्डली (D7)' : 'Saptamamsha (D7)', desc: isHi ? 'पुत्रपौत्रादिज्ञानम् (Progeny & Lineage)' : 'Children & Lineage', data: charts.D7 || charts.D1 },
    { code: 'D9', name: isHi ? 'नवमांश कुण्डली (D9)' : 'Navamsha Chart (D9)', desc: isHi ? 'कलत्र सौख्यम् (Dharma & Marriage)' : 'Spouse, Dharma & Destiny', data: charts.D9 || charts.D1 },
    { code: 'D10', name: isHi ? 'दशमांश कुण्डली (D10)' : 'Dashamsha Chart (D10)', desc: isHi ? 'राज्यविचारः (Career & Authority)' : 'Professional Zenith & Status', data: charts.D10 || charts.D1 },
    { code: 'D12', name: isHi ? 'द्वादशांश कुण्डली (D12)' : 'Dwadashamsha (D12)', desc: isHi ? 'पितृसौख्यम् (Ancestral Lineage)' : 'Parents & Heritage', data: charts.D12 || charts.D1 },
    { code: 'D16', name: isHi ? 'षोडशांश कुण्डली (D16)' : 'Shodashamsha (D16)', desc: isHi ? 'वाहनसुखविचारः (Conveyances & Comfort)' : 'Vehicles & Happiness', data: charts.D16 || charts.D1 },
    { code: 'D20', name: isHi ? 'विंशांश कुण्डली (D20)' : 'Vimshamsha (D20)', desc: isHi ? 'उपासनाज्ञानम् (Spiritual Devotion)' : 'Spiritual Evolution', data: charts.D20 || charts.D1 },
    { code: 'D24', name: isHi ? 'चतुर्विंशांश कुण्डली (D24)' : 'Chaturvimshamsha (D24)', desc: isHi ? 'विद्याविचारः (Higher Intellect)' : 'Higher Learning & Intellect', data: charts.D24 || charts.D1 },
    { code: 'D27', name: isHi ? 'सप्तविंशांश कुण्डली (D27)' : 'Saptavimshamsha (D27)', desc: isHi ? 'बलाबलज्ञानम् (Endurance & Vitality)' : 'Physical Strength & Stamina', data: charts.D27 || charts.D1 },
    { code: 'D30', name: isHi ? 'त्रिंशांश कुण्डली (D30)' : 'Trimshamsha (D30)', desc: isHi ? 'अरिष्टज्ञानम् (Karmic Afflictions)' : 'Misfortunes & Obstacles', data: charts.D30 || charts.D1 },
    { code: 'D60', name: isHi ? 'षष्ट्यंश कुण्डली (D60)' : 'Shashtyamsha (D60)', desc: isHi ? 'सर्वस्थिति विचारः (Deep Past Karma Root)' : 'Subtle Past Life Karma', data: charts.D60 || charts.D1 }
  ];

  const paperCssSize = paperSize === 'letter' ? 'letter' : paperSize === 'legal' ? 'legal' : paperSize === 'a3' ? 'A3' : 'A4';
  const containerMaxWidth = paperSize === 'a3' ? 'max-w-[1123px]' : paperSize === 'legal' ? 'max-w-[850px]' : paperSize === 'letter' ? 'max-w-[816px]' : 'max-w-[850px]';
  const pageMinHeightCss = paperSize === 'a3' ? '1580px' : paperSize === 'legal' ? '1340px' : paperSize === 'letter' ? '1050px' : '1120px';

  return (
    <div
      id="brihat-janmapatrika-container"
      className={`${containerBg} font-sans mx-auto ${containerMaxWidth} shadow-lg print:shadow-none print:max-w-none print:w-full print:bg-white relative`}
      style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
    >
      {/* GLOBAL PRINT STYLES */}
      <style>{`
        @media print {
          @page {
            size: ${paperCssSize} portrait;
            margin: 8mm 10mm 8mm 10mm;
          }
          body {
            background-color: ${isBw ? '#FFFFFF' : '#FFFDF7'} !important;
            color: ${isBw ? '#000000' : '#263332'} !important;
          }
          .dossier-page {
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
            min-height: ${pageMinHeightCss};
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            margin-bottom: 10mm;
          }
          .dossier-page:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .avoid-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `}</style>

      {/* REPEATING SWASTIKA MARGIN (White/Black in Eco mode, Gold in Deluxe) */}
      <div
        className="hidden print:flex fixed right-1 top-0 bottom-0 w-4 flex-col justify-around items-center text-xs font-bold select-none pointer-events-none z-50 opacity-70"
        style={{ color: isBw ? '#000000' : '#B58A4A' }}
        aria-hidden="true"
      >
        {Array.from({ length: 32 }).map((_, i) => (
          <span key={i} className="leading-none">卐</span>
        ))}
      </div>

      {reportScope === 'GRAND' ? (
        <GranthaPages
          kundali={kundali}
          lang={activeLang}
          isBw={isBw}
          pageBorder={pageBorder}
          runningHeaderBorder={runningHeaderBorder}
          headingPrimary={headingPrimary}
          headingAccent={headingAccent}
          innerCardBg={innerCardBg}
          tableBorder={tableBorder}
          tableHeaderBg={tableHeaderBg}
          tableRowEven={tableRowEven}
          tableRowOdd={tableRowOdd}
          mutedText={mutedText}
          renderPageFooter={renderPageFooter}
          activeGrantha={activeGrantha}
          chartStyle={chartStyle}
          branding={activeBranding}
        />
      ) : (
        <>
          {/* ========================================================================= */}
          {/* PAGE 1: SACRED TITLE & INVOCATIONS (COVER)                                */}
          {/* ========================================================================= */}
          <div className={`dossier-page min-h-[1050px] p-8 sm:p-12 ${pageBorder} relative avoid-break ${isBw ? 'bg-white' : 'bg-[#FFFDF7]'}`}>
            <div className={`absolute inset-2 border pointer-events-none ${isBw ? 'border-black' : 'border-[#B58A4A]/40'}`} />

        <div className="text-center space-y-2 z-10">
          <p className={`text-xs font-bold tracking-widest uppercase ${headingPrimary}`}>
            {activeBranding.customHeaderSlogan || (isHi ? 'वेद चक्षुः समं ज्योतिः • वैदिक जन्मपत्रिका' : 'VEDIC HOROSCOPE COMPENDIUM')}
          </p>
          <h1 className={`text-3xl font-bold font-serif ${headingPrimary}`}>
            {birthDetails.name || (isHi ? 'जातक' : 'Native')}
          </h1>
        </div>

        {/* Sacred Invocations */}
        <div className="my-6 z-10 text-center space-y-3">
          <div className={`flex items-center justify-center gap-6 text-base font-bold ${headingPrimary}`}>
            <span>शुभ</span>
            <div className={`inline-flex items-center justify-center p-2 rounded-full border ${isBw ? 'border-black' : 'border-[#174C4B]/40 bg-[#E8F0EF]'}`}>
              <span className={`text-2xl leading-none ${headingAccent}`}>卐</span>
            </div>
            <span>लाभ</span>
          </div>

          <div className={`text-2xl font-bold font-serif ${headingPrimary}`}>
            ॐ गं गणपतये नमः
          </div>

          <div className={`max-w-xl mx-auto p-4 rounded-2xl border space-y-2 text-center ${innerCardBg}`}>
            <div className={`text-[10px] uppercase font-bold tracking-wider ${headingAccent}`}>
              — श्री विष्णु सहस्रनाम स्तोत्रम् (मांगलिक प्रार्थना) —
            </div>
            <pre className={`text-sm font-serif font-bold whitespace-pre-line leading-relaxed ${headingPrimary}`}>
              {VISHNU_SAHASRANAMA_INVOCATIONS[0].shlokaSanskrit}
            </pre>
            <p className={`text-xs italic pt-1 border-t leading-normal ${mutedText} ${isBw ? 'border-black' : 'border-[#E5DDD0]'}`}>
              {vishnuBrief}
            </p>
          </div>
        </div>

        {/* Dossier Title Emblem */}
        <div className="my-auto z-10 text-center py-4">
          <div className={`inline-block p-6 rounded-3xl border-2 max-w-md w-full ${isBw ? 'bg-white border-black' : 'bg-gradient-to-b from-[#F7F3EA] to-[#F2EFE7] border-[#174C4B] shadow-sm'}`}>
            <div className={`text-xs uppercase font-bold tracking-widest mb-1 ${headingAccent}`}>
              {reportScope === 'CAREER'
                ? (isHi ? 'कर्म एवं अर्थ संपदा डायग्नोस्टिक' : 'CAREER & WEALTH DOSSIER')
                : reportScope === 'MARRIAGE'
                ? (isHi ? 'विवाह एवं दाम्पत्य सौख्यम् डायग्नोस्टिक' : 'MARRIAGE & RELATIONSHIP DOSSIER')
                : reportScope === 'REMEDIAL'
                ? (isHi ? 'लाल किताब एवं दैव उपाय संकलन' : 'LAL KITAB & REMEDIAL DOSSIER')
                : reportScope === 'SUMMARY'
                ? (isHi ? 'संक्षिप्त जन्मपत्रिका' : 'JANMAPATRI SUMMARY')
                : (isHi ? 'वृहत् जन्मपत्रिका (भृगु संहिता स्वरूप)' : 'BRIHAT BHRIGU PATRIKA')}
            </div>
            <div className={`text-xl font-bold font-serif ${headingPrimary}`}>
              {birthDetails.name || 'Jataka Profile'}
            </div>
            <div className={`text-xs mt-2 font-mono ${mutedText}`}>
              {formatDOB(birthDetails.date)} • {birthDetails.time || '07:40 AM'}
            </div>
            <div className={`text-xs font-serif ${mutedText}`}>
              {birthDetails.placeName || 'Rohtak, India'}
            </div>
          </div>
        </div>

        {/* Branding Footer */}
        <div className="z-10 text-center space-y-1">
          {activeBranding.name && (
            <div className={`text-sm font-bold font-serif tracking-wide ${headingPrimary}`}>
              {activeBranding.name}
            </div>
          )}
          {activeBranding.title && (
            <div className={`text-xs font-semibold ${headingAccent}`}>
              ({activeBranding.title})
            </div>
          )}
          {(activeBranding.sansthan || activeBranding.location) && (
            <div className={`text-[11px] ${mutedText}`}>
              {[activeBranding.sansthan, activeBranding.location].filter(Boolean).join(' • ')}
            </div>
          )}
          {activeBranding.phone && (
            <div className={`text-[10px] font-mono ${mutedText}`}>
              WhatsApp: {activeBranding.phone}
            </div>
          )}
        </div>

        {renderPageFooter(isHi ? 'मुखपृष्ठ' : 'Cover')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 2: REPORT INDEX & TABLE OF CONTENTS                                  */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-4 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'अनुक्रमणिका एवं विषय सूचि' : 'Report Index & Table of Contents'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ २' : 'Page 2'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'वृहत् जन्मपत्रिका — विषय अनुक्रमणिका (Table of Contents)' : 'BRIHAT JANMAPATRIKA — REPORT INDEX'}
            </h2>
          </div>

          <div className={`rounded-2xl p-5 border space-y-3 ${innerCardBg}`}>
            {[
              { page: 1, titleEn: 'Cover & Sacred Invocations', titleHi: 'मुखपृष्ठ एवं मांगलिक स्तोत्र' },
              { page: 2, titleEn: 'Report Index & Table of Contents', titleHi: 'अनुक्रमणिका एवं विषय सूचि' },
              { page: 3, titleEn: 'Foundational Trinity (Lagna D1, Moon, Navamsha D9)', titleHi: 'प्रधान कुण्डली त्रयी (लग्न, चन्द्र, नवांश)' },
              { page: 4, titleEn: 'Birth Telemetry, Ishta Kaal & Avakahada Coordinates', titleHi: 'जन्म पंचांग, इष्ट काल एवं अवकहड़ा चक्र' },
              { page: 5, titleEn: 'Planetary Ephemeris, Motion Speed & 12 Bhava Cusps', titleHi: 'ग्रह स्पष्ट, गति, अवस्था एवं भाव स्पष्ट' },
              { page: 6, titleEn: 'Chalit, Bhav Dynamics, 27 Tara Chakra & KP Sub-Lords', titleHi: 'चलित, भाव चक्र, तारा चक्र एवं केपी सब-लॉर्ड' },
              { page: 7, titleEn: 'Authentic Sudarshan Chakra (Full Page Mandala)', titleHi: 'प्रामाणिक सुदर्शन चक्र महामण्डल' },
              { page: 8, titleEn: 'Shodashvarga Chakra — Part I (D1, D2, D3, D4)', titleHi: 'षोडशवर्ग चक्र — भाग १' },
              { page: 9, titleEn: 'Shodashvarga Chakra — Part II (D7, D9, D10, D12)', titleHi: 'षोडशवर्ग चक्र — भाग २' },
              { page: 10, titleEn: 'Shodashvarga Chakra — Part III (D16, D20, D24, D27)', titleHi: 'षोडशवर्ग चक्र — भाग ३' },
              { page: 11, titleEn: 'Shadbala Potency Scores & Sarvashtakavarga Matrix', titleHi: 'षड्बल सामर्थ्य एवं सर्वाष्टकवर्ग सारिणी' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-[#E5DDD0]/70 text-xs sm:text-sm">
                <span className={`font-serif font-bold ${headingPrimary}`}>
                  {isHi ? item.titleHi : item.titleEn}
                </span>
                <span className="font-mono font-bold text-astro-primary bg-astro-primary/10 px-2.5 py-0.5 rounded-full border border-astro-primary/20">
                  {isHi ? `पृष्ठ ${item.page}` : `Page ${item.page}`}
                </span>
              </div>
            ))}
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ २' : 'Page 2')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 3: SIDE-BY-SIDE FOUNDATIONAL CHARTS (BHRIGU PATRIKA ARCHITECTURE)   */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'मूल त्रिकोण कुण्डली (लग्न, चन्द्र, नवमांश)' : 'Foundational Trinity (Lagna, Moon, Navamsha)'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३' : 'Page 3'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्रधान कुण्डली विचार — समकालिक पार्श्व स्वरूप' : 'FOUNDATIONAL KUNDALIS (SIDE-BY-SIDE MASTER BLUEPRINT)'}
            </h2>
            <p className={`text-xs ${mutedText}`}>
              {isHi
                ? 'लग्न कुण्डली (स्थूल शरीर), चन्द्र कुण्डली (मनोबल व मानसिक स्थिति) एवं नवमांश कुण्डली (सूक्ष्म भाग्य व धर्म कलत्र) का प्रामाणिक पार्श्व समन्वय।'
                : 'Canonical side-by-side architecture mirroring classical Bhrigu Patrika compendiums: Lagna D1 (left) synchronized with Chandra and Navamsha (right).'}
            </p>
          </div>

          {/* SIDE-BY-SIDE 2-COLUMN LAYOUT AS IN BHRIGU PATRIKA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            {/* Left Column: Master Lagna Kundali (D1) with SAV */}
            <div className={`rounded-2xl p-4 border space-y-2 flex flex-col items-center justify-between ${innerCardBg}`}>
              <div className="text-center w-full border-b pb-1.5 flex items-center justify-between px-1">
                <div>
                  <span className={`text-sm font-bold uppercase font-serif block text-left ${headingPrimary}`}>
                    {isHi ? '१. लग्न कुण्डली (D1)' : '1. Lagna Kundali (D1)'}
                  </span>
                  <span className={`text-xs block text-left ${mutedText}`}>{isHi ? 'देह, स्थूल स्वरूप एवं भौतिक जीवन' : 'Physical Self & Constitution'}</span>
                </div>
                <span className="text-[10px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                  {isHi ? 'सर्वाष्टकवर्ग (SAV)' : 'SAV Bindus'}
                </span>
              </div>
              <div className="w-full flex justify-center py-2">
                {renderLargeChart(charts.D1, undefined, true)}
              </div>
              <div className="w-full text-center text-xs font-mono border-t pt-2 flex items-center justify-between px-2">
                <span className="font-bold text-astro-primary">Lagna: {ascendant.sign}</span>
                <span className="font-bold font-mono text-sm">{ascendant.degreeFormatted}</span>
                <span className="font-semibold text-gray-700">{ascendant.nakshatra} ({ascendant.pada})</span>
              </div>
            </div>

            {/* Right Column: Stacked Side-by-Side Chandra & Navamsha Kundalis */}
            <div className="flex flex-col gap-3.5 justify-between">
              {/* 2. Chandra Kundali (Moon) */}
              <div className={`rounded-2xl p-3 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
                <div className="flex items-center justify-between w-full border-b pb-1 px-1">
                  <span className={`text-xs sm:text-sm font-bold uppercase font-serif ${headingPrimary}`}>
                    {isHi ? '२. चन्द्र कुण्डली (Moon)' : '2. Chandra Kundali (Moon)'}
                  </span>
                  <span className={`text-xs font-mono font-bold ${headingAccent}`}>
                    {moonPlanet?.sign || 'Leo'} {moonPlanet?.degreeFormatted || "28°42'"}
                  </span>
                </div>
                <div className="w-full flex justify-center py-0.5">
                  {renderVargaChart(charts.Chandra || charts.D1)}
                </div>
                <div className="w-full text-center text-xs text-gray-700 italic border-t pt-1">
                  {isHi ? 'मनोबल, विचार एवं मानसिक संवेदना' : 'Mental Clarity & Psychic Resonance'}
                </div>
              </div>

              {/* 3. Navamsha Kundali (D9) */}
              <div className={`rounded-2xl p-3 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
                <div className="flex items-center justify-between w-full border-b pb-1 px-1">
                  <span className={`text-xs sm:text-sm font-bold uppercase font-serif ${headingPrimary}`}>
                    {isHi ? '३. नवमांश कुण्डली (D9)' : '3. Navamsha Kundali (D9)'}
                  </span>
                  <span className={`text-xs font-mono font-bold ${headingAccent}`}>
                    Nav. Lagna: {charts.D9?.ascendant?.sign || 'Sagittarius'}
                  </span>
                </div>
                <div className="w-full flex justify-center py-0.5">
                  {renderVargaChart(charts.D9 || charts.D1)}
                </div>
                <div className="w-full text-center text-xs text-gray-700 italic border-t pt-1">
                  {isHi ? 'सूक्ष्म भाग्य, कलत्र सौख्यम् एवं धर्म' : 'Destiny, Marriage & Soul Purpose'}
                </div>
              </div>
            </div>
          </div>

          {/* Core Alignment Highlights Strip */}
          <div className={`rounded-xl p-3.5 border grid grid-cols-3 gap-2 text-center text-xs sm:text-sm ${innerCardBg}`}>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'जन्म लग्न' : 'Ascendant'}</span>
              <span className="font-bold text-sm text-astro-primary">{ascendant.sign} ({ascendant.degreeFormatted})</span>
            </div>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'चन्द्र राशि' : 'Moon Rashi'}</span>
              <span className="font-bold text-sm text-astro-primary">{moonPlanet?.sign || 'Leo'} ({moonPlanet?.nakshatra || 'U.Phalguni'})</span>
            </div>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'सूर्य राशि' : 'Sun Rashi'}</span>
              <span className="font-bold text-sm text-astro-primary">{sunPlanet?.sign || 'Pisces'} ({sunPlanet?.nakshatra || 'Revati'})</span>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ३' : 'Page 3')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 4: BIRTH TELEMETRY, ISHTA KAAL & AVAKAHADA COORDINATES               */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        {/* Running Header */}
        <div className={`pb-2 mb-4 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'जन्म पंचांग एवं अवकहड़ा चक्र' : 'Birth Telemetry & Avakahada Coordinates'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ४' : 'Page 4'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'जन्म समय गणित, इष्ट काल एवं अवकहड़ा चक्र' : 'BIRTH TELEMETRY, ISHTA KAAL & AVAKAHADA COORDINATES'}
            </h2>
          </div>

          {/* Top 2 Columns: Telemetry & Avakahada */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Box A: Precision Time Telemetry */}
            <div className={`rounded-xl p-4 space-y-2.5 ${innerCardBg}`}>
              <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary} ${isBw ? 'border-black' : 'border-[#E5DDD0]'}`}>
                {isHi ? 'जन्म समय एवं इष्ट काल (Precision Time)' : 'Precision Time Telemetry'}
              </h3>
              <div className="grid grid-cols-2 gap-y-2 text-xs sm:text-sm">
                <span className={mutedText}>{isHi ? 'इष्ट काल:' : 'Ishta Kaal:'}</span>
                <span className="font-mono font-bold text-astro-primary">{ishtaKaal.ishtaKaalFormatted}</span>

                <span className={mutedText}>{isHi ? 'दिनमान (Day Duration):' : 'Dinmaan:'}</span>
                <span className="font-mono font-semibold">{ishtaKaal.dinmaanGhati}</span>

                <span className={mutedText}>{isHi ? 'रात्रिमान (Night Duration):' : 'Ratrimaan:'}</span>
                <span className="font-mono font-semibold">{ishtaKaal.ratrimaanGhati}</span>

                <span className={mutedText}>{isHi ? 'स्थानिक संस्कार (LMT Offset):' : 'LMT Offset:'}</span>
                <span className="font-mono">{ishtaKaal.sthanikSanskar}</span>

                <span className={mutedText}>{isHi ? 'स्थानिक समय (LMT):' : 'LMT Birth Time:'}</span>
                <span className="font-mono font-bold">{ishtaKaal.sthanikSamaya}</span>

                <span className={mutedText}>{isHi ? 'वेलांतर (Equation of Time):' : 'Equation of Time:'}</span>
                <span className="font-mono">{ishtaKaal.velantar}</span>

                <span className={mutedText}>{isHi ? 'सूर्योदय (Sunrise):' : 'Local Sunrise:'}</span>
                <span className="font-mono font-semibold">{ishtaKaal.sunriseFormatted}</span>

                <span className={mutedText}>{isHi ? 'सूर्यास्त (Sunset):' : 'Local Sunset:'}</span>
                <span className="font-mono font-semibold">{ishtaKaal.sunsetFormatted}</span>

                <span className={mutedText}>{isHi ? 'सूर्य स्थिति (अयन):' : 'Solar Ayana:'}</span>
                <span className="font-semibold text-astro-primary">{ishtaKaal.ayana}</span>

                <span className={mutedText}>{isHi ? 'ऋतु (Season):' : 'Ritu:'}</span>
                <span className="font-medium">{ishtaKaal.ritu}</span>
              </div>
            </div>

            {/* Box B: Avakahada Chakra */}
            <div className={`rounded-xl p-4 space-y-2.5 ${innerCardBg}`}>
              <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary} ${isBw ? 'border-black' : 'border-[#E5DDD0]'}`}>
                {isHi ? 'अवकहड़ा चक्र (Personality Coordinates)' : 'Avakahada Harmonic Factors'}
              </h3>
              <div className="grid grid-cols-2 gap-y-2 text-xs sm:text-sm">
                <span className={mutedText}>{isHi ? 'लग्न / राशि स्वामी:' : 'Lagna / Rashi Lord:'}</span>
                <span className="font-bold text-astro-primary">{avakahadaData.rashiLord}</span>

                <span className={mutedText}>{isHi ? 'नक्षत्र स्वामी:' : 'Star Lord:'}</span>
                <span className="font-bold">{avakahadaData.nakshatraLord}</span>

                <span className={mutedText}>{isHi ? 'वर्ण (Varna):' : 'Varna:'}</span>
                <span className="font-medium">{avakahadaData.varna}</span>

                <span className={mutedText}>{isHi ? 'वश्य (Vashya):' : 'Vashya:'}</span>
                <span className="font-medium">{avakahadaData.vashya}</span>

                <span className={mutedText}>{isHi ? 'योनि (Yoni):' : 'Yoni:'}</span>
                <span className="font-medium">{avakahadaData.taraYoni}</span>

                <span className={mutedText}>{isHi ? 'गण (Gana):' : 'Gana:'}</span>
                <span className="font-medium">{avakahadaData.gana}</span>

                <span className={mutedText}>{isHi ? 'नाड़ी (Nadi):' : 'Nadi:'}</span>
                <span className="font-medium">{avakahadaData.nadi}</span>

                <span className={mutedText}>{isHi ? 'हंसक (Tattva):' : 'Hansak (Element):'}</span>
                <span className="font-medium">{avakahadaData.hansak}</span>

                <span className={mutedText}>{isHi ? 'पाया (Paya):' : 'Paya:'}</span>
                <span className="font-medium">{avakahadaData.paya}</span>

                <span className={mutedText}>{isHi ? 'जन्म नामाक्षर:' : 'Auspicious Initial:'}</span>
                <span className="font-bold text-astro-primary text-base font-serif">{avakahadaData.namakshar}</span>
              </div>
            </div>
          </div>

          {/* Ghat Chakra Sensitivity Coordinates */}
          <div className={`rounded-xl p-4 space-y-2.5 ${innerCardBg}`}>
            <div className="flex items-center justify-between border-b pb-1.5">
              <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
                {isHi ? 'घात चक्र (Astrological Avoidance Indicators)' : 'Ghat Chakra Sensitivity Coordinates'}
              </h3>
              <span className={`text-xs italic ${mutedText}`}>
                {isHi ? 'शुभ कार्य में इन तिथियों, वारों व नक्षत्रों का परिहार श्रेयस्कर है।' : 'Classical inauspicious alignments to avoid for high-stakes undertakings.'}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs sm:text-sm text-center">
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात मास' : 'Ghat Month'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatMaas}</span>
              </div>
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात तिथि' : 'Ghat Tithi'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatTithi}</span>
              </div>
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात वार' : 'Ghat Day'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatVaar}</span>
              </div>
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात नक्षत्र' : 'Ghat Star'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatNakshatra}</span>
              </div>

              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात प्रहर' : 'Ghat Prahar'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatPrahar} प्रहर</span>
              </div>
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात वर्ग' : 'Ghat Varga'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatVarga}</span>
              </div>
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात लग्न' : 'Ghat Lagna'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatLagna}</span>
              </div>
              <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-xs font-bold ${mutedText}`}>{isHi ? 'घात करण / योग' : 'Ghat Karana/Yoga'}</span>
                <span className="font-bold text-sm text-astro-primary">{ghatChakraData.ghatKarana} / {ghatChakraData.ghatYoga}</span>
              </div>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ४' : 'Page 4')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 3: SIDE-BY-SIDE FOUNDATIONAL CHARTS (BHRIGU PATRIKA ARCHITECTURE)   */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'मूल त्रिकोण कुण्डली (लग्न, चन्द्र, नवमांश)' : 'Foundational Trinity (Lagna, Moon, Navamsha)'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ३' : 'Page 3'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्रधान कुण्डली विचार — समकालिक पार्श्व स्वरूप' : 'FOUNDATIONAL KUNDALIS (SIDE-BY-SIDE MASTER BLUEPRINT)'}
            </h2>
            <p className={`text-xs ${mutedText}`}>
              {isHi
                ? 'लग्न कुण्डली (स्थूल शरीर), चन्द्र कुण्डली (मनोबल व मानसिक स्थिति) एवं नवमांश कुण्डली (सूक्ष्म भाग्य व धर्म कलत्र) का प्रामाणिक पार्श्व समन्वय।'
                : 'Canonical side-by-side architecture mirroring classical Bhrigu Patrika compendiums: Lagna D1 (left) synchronized with Chandra and Navamsha (right).'}
            </p>
          </div>

          {/* SIDE-BY-SIDE 2-COLUMN LAYOUT AS IN BHRIGU PATRIKA */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
            {/* Left Column: Master Lagna Kundali (D1) with SAV */}
            <div className={`rounded-2xl p-4 border space-y-2 flex flex-col items-center justify-between ${innerCardBg}`}>
              <div className="text-center w-full border-b pb-1.5 flex items-center justify-between px-1">
                <div>
                  <span className={`text-sm font-bold uppercase font-serif block text-left ${headingPrimary}`}>
                    {isHi ? '१. लग्न कुण्डली (D1)' : '1. Lagna Kundali (D1)'}
                  </span>
                  <span className={`text-xs block text-left ${mutedText}`}>{isHi ? 'देह, स्थूल स्वरूप एवं भौतिक जीवन' : 'Physical Self & Constitution'}</span>
                </div>
                <span className="text-[10px] font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                  {isHi ? 'सर्वाष्टकवर्ग (SAV)' : 'SAV Bindus'}
                </span>
              </div>
              <div className="w-full flex justify-center py-2">
                {renderLargeChart(charts.D1, undefined, true)}
              </div>
              <div className="w-full text-center text-xs font-mono border-t pt-2 flex items-center justify-between px-2">
                <span className="font-bold text-astro-primary">Lagna: {ascendant.sign}</span>
                <span className="font-bold font-mono text-sm">{ascendant.degreeFormatted}</span>
                <span className="font-semibold text-gray-700">{ascendant.nakshatra} ({ascendant.pada})</span>
              </div>
            </div>

            {/* Right Column: Stacked Side-by-Side Chandra & Navamsha Kundalis */}
            <div className="flex flex-col gap-3.5 justify-between">
              {/* 2. Chandra Kundali (Moon) */}
              <div className={`rounded-2xl p-3 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
                <div className="flex items-center justify-between w-full border-b pb-1 px-1">
                  <span className={`text-xs sm:text-sm font-bold uppercase font-serif ${headingPrimary}`}>
                    {isHi ? '२. चन्द्र कुण्डली (Moon)' : '2. Chandra Kundali (Moon)'}
                  </span>
                  <span className={`text-xs font-mono font-bold ${headingAccent}`}>
                    {moonPlanet?.sign || 'Leo'} {moonPlanet?.degreeFormatted || "28°42'"}
                  </span>
                </div>
                <div className="w-full flex justify-center py-0.5">
                  {renderVargaChart(charts.Chandra || charts.D1)}
                </div>
                <div className="w-full text-center text-xs text-gray-700 italic border-t pt-1">
                  {isHi ? 'मनोबल, विचार एवं मानसिक संवेदना' : 'Mental Clarity & Psychic Resonance'}
                </div>
              </div>

              {/* 3. Navamsha Kundali (D9) */}
              <div className={`rounded-2xl p-3 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
                <div className="flex items-center justify-between w-full border-b pb-1 px-1">
                  <span className={`text-xs sm:text-sm font-bold uppercase font-serif ${headingPrimary}`}>
                    {isHi ? '३. नवमांश कुण्डली (D9)' : '3. Navamsha Kundali (D9)'}
                  </span>
                  <span className={`text-xs font-mono font-bold ${headingAccent}`}>
                    Nav. Lagna: {charts.D9?.ascendant?.sign || 'Sagittarius'}
                  </span>
                </div>
                <div className="w-full flex justify-center py-0.5">
                  {renderVargaChart(charts.D9 || charts.D1)}
                </div>
                <div className="w-full text-center text-xs text-gray-700 italic border-t pt-1">
                  {isHi ? 'सूक्ष्म भाग्य, कलत्र सौख्यम् एवं धर्म' : 'Destiny, Marriage & Soul Purpose'}
                </div>
              </div>
            </div>
          </div>

          {/* Core Alignment Highlights Strip */}
          <div className={`rounded-xl p-3.5 border grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs sm:text-sm ${innerCardBg}`}>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'जन्म लग्न' : 'Ascendant'}</span>
              <span className="font-bold text-sm text-astro-primary">{ascendant.sign} ({ascendant.degreeFormatted})</span>
            </div>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'चन्द्र राशि' : 'Moon Rashi'}</span>
              <span className="font-bold text-sm text-astro-primary">{moonPlanet?.sign || 'Leo'} ({moonPlanet?.nakshatra || 'U.Phalguni'})</span>
            </div>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'सूर्य राशि' : 'Sun Rashi'}</span>
              <span className="font-bold text-sm text-astro-primary">{sunPlanet?.sign || 'Pisces'} ({sunPlanet?.nakshatra || 'Revati'})</span>
            </div>
            <div>
              <span className={`block text-xs font-bold uppercase ${mutedText}`}>{isHi ? 'पात गणना' : 'Node Mode'}</span>
              <span className="font-bold text-xs text-astro-primary font-mono">
                {(kundali.nodeConvention || kundali.birthDetails?.nodeConvention) === 'true'
                  ? (isHi ? 'वास्तविक पात' : 'True Node')
                  : (isHi ? 'मध्यम पात' : 'Mean Node')}
              </span>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ३' : 'Page 3')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 4: PLANETARY EPHEMERIS, MOTION SPEED & 12 BHAVA CUSPS                 */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'ग्रह स्पष्ट एवं भाव मध्य सारिणी' : 'Planetary Ephemeris & House Cusps'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ४' : 'Page 4'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'ग्रह स्पष्ट, गति, अवस्था एवं दीप्तादि विचार' : 'PLANETARY POSITIONS, MOTION & DIGNITIES'}
            </h2>
            <div className="flex items-center justify-center gap-2 text-[10px] text-astro-secondary-text font-mono">
              <span>{isHi ? 'अयनांश: लाहिरी (चित्रा पक्ष)' : 'Ayanamsha: Lahiri (Chitra Paksha)'}</span>
              <span>•</span>
              <span className="font-semibold text-astro-primary">
                {isHi
                  ? `राहु/केतु: ${(kundali.nodeConvention || kundali.birthDetails?.nodeConvention) === 'true' ? 'वास्तविक पात (True Node)' : 'मध्यम पात (Mean Node - Classical)'}`
                  : `Rahu/Ketu: ${(kundali.nodeConvention || kundali.birthDetails?.nodeConvention) === 'true' ? 'True / Osculating Node' : 'Mean Node (Canonical Default)'}`}
              </span>
            </div>
          </div>

          {/* Ephemeris Table with Calibrated Column Widths (Zero Clipping of Dignity or Longitude!) */}
          <div className={`rounded-2xl border ${tableBorder} overflow-x-auto`}>
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className={tableHeaderBg}>
                  <th className="w-[13%] p-2 font-bold">{isHi ? 'ग्रह' : 'Body'}</th>
                  <th className="w-[6%] p-2 font-bold text-center">{isHi ? 'व/अ' : 'R/C'}</th>
                  <th className="w-[11%] p-2 font-bold">{isHi ? 'राशि' : 'Rashi'}</th>
                  <th className="w-[16%] p-2 font-bold font-mono">{isHi ? 'अंश' : 'Longitude'}</th>
                  <th className="w-[10%] p-2 font-bold font-mono">{isHi ? 'गति' : 'Motion'}</th>
                  <th className="w-[17%] p-2 font-bold">{isHi ? 'नक्षत्र' : 'Nakshatra'}</th>
                  <th className="w-[5%] p-2 font-bold text-center">{isHi ? 'पद' : 'Pada'}</th>
                  <th className="w-[10%] p-2 font-bold">{isHi ? 'बाल्यादि' : 'Avastha'}</th>
                  <th className="w-[12%] p-2 font-bold">{isHi ? 'दीप्तादि' : 'Dignity'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5DDD0]">
                {/* Ascendant Row */}
                <tr className={tableRowEven}>
                  <td className="p-2 font-bold text-astro-primary whitespace-nowrap">{isHi ? 'लग्न (Lagna)' : 'Ascendant'}</td>
                  <td className="p-2 text-center font-mono text-gray-400">—</td>
                  <td className="p-2 font-semibold whitespace-nowrap">{ascendant.sign}</td>
                  <td className="p-2 font-mono font-bold text-astro-primary text-xs whitespace-nowrap">{ascendant.degreeFormatted}</td>
                  <td className="p-2 font-mono text-xs whitespace-nowrap text-gray-600">01:00:15</td>
                  <td className="p-2 font-medium whitespace-nowrap">{ascendant.nakshatra}</td>
                  <td className="p-2 text-center font-mono font-bold">{ascendant.pada}</td>
                  <td className="p-2 whitespace-nowrap">{isHi ? 'युवा (Yuva)' : 'Yuva'}</td>
                  <td className="p-2 font-semibold text-emerald-800 whitespace-nowrap">{isHi ? 'शुभ (Auspicious)' : 'Auspicious'}</td>
                </tr>
                {/* 9 Planets (Filter out Ascendant to avoid duplicate rendering) */}
                {planets.filter(p => p.name !== 'Ascendant' && (p.name as string) !== 'Lagna').map((p, idx) => {
                  const isRet = p.isRetrograde;
                  const isComb = p.isCombust;
                  const avastha = avasthas?.[p.name]?.baladi || (idx % 2 === 0 ? 'Yuva' : 'Baal');
                  const dignity = avasthas?.[p.name]?.jagradadi || (p.name === 'Jupiter' ? 'Mudita' : 'Deepta');

                  return (
                    <tr key={idx} className={idx % 2 === 0 ? tableRowOdd : tableRowEven}>
                      <td className="p-2 font-bold text-astro-primary whitespace-nowrap">
                        {getPlanetFullName(p.name, activeLang)}
                      </td>
                      <td className="p-2 text-center font-mono text-xs whitespace-nowrap">
                        {isRet && <span className="text-amber-800 font-bold">व (R)</span>}
                        {isComb && <span className="text-rose-800 font-bold ml-1">अ (C)</span>}
                        {!isRet && !isComb && <span className="text-gray-400">—</span>}
                      </td>
                      <td className="p-2 font-semibold whitespace-nowrap">{p.sign}</td>
                      <td className="p-2 font-mono font-bold text-xs whitespace-nowrap">{p.degreeFormatted}</td>
                      <td className="p-2 font-mono text-gray-700 text-xs whitespace-nowrap">{p.speed !== undefined ? `${p.speed.toFixed(2)}°/d` : '01:00:15'}</td>
                      <td className="p-2 font-medium whitespace-nowrap">{p.nakshatra}</td>
                      <td className="p-2 text-center font-mono font-bold">{p.pada}</td>
                      <td className="p-2 whitespace-nowrap">{avastha}</td>
                      <td className="p-2 font-semibold text-emerald-800 whitespace-nowrap">{dignity}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Nirayan Bhava Chalit Cusps (12 Houses) */}
          <div className={`rounded-2xl p-4 space-y-2.5 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
              {isHi ? 'निरायन भाव चलित मध्य एवं संधि स्पष्ट (12 Houses)' : 'NIRAYAN BHAVA CHALIT CUSPS & MID-POINTS'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs sm:text-sm">
              {(houses.length > 0 ? houses : Array.from({ length: 12 })).map((h: any, i: number) => {
                const houseNum = i + 1;
                const signName = h?.sign || ['Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces', 'Aries'][i];
                const degreeStr = h?.degreeFormatted || "20°48'41\"";

                return (
                  <div key={i} className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-astro-primary">{isHi ? `भाव ${houseNum}` : `Bhava ${houseNum}`}</span>
                      <span className={`font-semibold ${mutedText}`}>{signName}</span>
                    </div>
                    <div className="font-mono text-xs sm:text-sm font-bold text-center mt-1 text-astro-primary">
                      {degreeStr}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ४' : 'Page 4')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 5: BHAVA & CHALIT DYNAMICS, 27 TARA CHAKRA & KP SUB-LORDS            */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'चलित, भाव एवं तारा चक्र' : 'Chalit, Bhav & Tara Chakra'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ५' : 'Page 5'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'चलित कुण्डली, भाव कुण्डली एवं २७ तारा चक्र' : 'CHALIT KUNDALI, BHAV SPANS & 27 TARA CHAKRA'}
            </h2>
          </div>

          {/* 2 Side-by-Side Large Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`rounded-2xl p-4 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
              <div className="text-center w-full border-b pb-1">
                <span className={`text-sm font-bold uppercase font-serif ${headingPrimary}`}>
                  {isHi ? 'चलित कुण्डली (Chalit Chart)' : 'Chalit Chart (Equal Shift)'}
                </span>
                <span className={`text-xs block ${mutedText}`}>{isHi ? 'ग्रहों का भाव संधि अनुसार विस्थापन' : 'Planetary displacement across house cusps'}</span>
              </div>
              <div className="w-full flex justify-center py-2">
                {renderVargaChart(charts.Chalit || charts.D1)}
              </div>
            </div>

            <div className={`rounded-2xl p-4 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
              <div className="text-center w-full border-b pb-1">
                <span className={`text-sm font-bold uppercase font-serif ${headingPrimary}`}>
                  {isHi ? 'भाव कुण्डली (Bhav Chart)' : 'Bhav Spans Chart'}
                </span>
                <span className={`text-xs block ${mutedText}`}>{isHi ? 'भाव मध्य एवं दृष्टि सम्बन्ध' : 'Mid-point occupancies and aspects'}</span>
              </div>
              <div className="w-full flex justify-center py-2">
                {renderVargaChart(charts.Bhav || charts.D1)}
              </div>
            </div>
          </div>

          {/* Complete 27 Tara Chakra Matrix (9-Column × 3-Paryaya Canonical Matrix) */}
          <div className={`rounded-2xl p-3 sm:p-4 space-y-2 border ${innerCardBg}`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm font-serif ${headingPrimary}`}>
                {isHi ? '२७ तारा चक्र (त्रिविध पर्याय वर्गीकरण)' : '27 TARA CHAKRA MATRIX (3-PARYAYA CLASSIFICATION)'}
              </h3>
              <span className={`text-[10px] font-mono ${mutedText}`}>
                {isHi ? `जन्म नक्षत्र: ${moonPlanet?.nakshatra || 'U.Phalguni'}` : `Janma Star: ${moonPlanet?.nakshatra || 'U.Phalguni'}`}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-center text-[10px] border-collapse table-fixed">
                <thead>
                  <tr className={tableHeaderBg}>
                    <th className="w-[12%] p-1 border border-[#E5DDD0] font-bold text-[9px]">{isHi ? 'पर्याय' : 'Cycle'}</th>
                    {[
                      { name: 'Janma', hi: 'जन्म', quality: isHi ? 'देह' : 'Life', ok: 'neutral' },
                      { name: 'Sampat', hi: 'सम्पत्', quality: isHi ? 'धन' : 'Wealth', ok: 'good' },
                      { name: 'Vipat', hi: 'विपत्', quality: isHi ? 'बाधा' : 'Peril', ok: 'bad' },
                      { name: 'Kshema', hi: 'क्षेम', quality: isHi ? 'कल्याण' : 'Safe', ok: 'good' },
                      { name: 'Pratyari', hi: 'प्रत्यरि', quality: isHi ? 'शत्रु' : 'Obstacle', ok: 'bad' },
                      { name: 'Sadhak', hi: 'साधक', quality: isHi ? 'सिद्धि' : 'Siddhi', ok: 'good' },
                      { name: 'Vadha', hi: 'वध', quality: isHi ? 'अशुभ' : 'Danger', ok: 'bad' },
                      { name: 'Mitra', hi: 'मित्र', quality: isHi ? 'सुहृद' : 'Friend', ok: 'good' },
                      { name: 'Atimitra', hi: 'अति-मित्र', quality: isHi ? 'परम' : 'Great', ok: 'good' }
                    ].map((cat, cIdx) => (
                      <th
                        key={cIdx}
                        className={`w-[9.7%] p-1 border border-[#E5DDD0] font-bold ${
                          cat.ok === 'good'
                            ? 'text-emerald-900 bg-emerald-50/70'
                            : cat.ok === 'bad'
                            ? 'text-rose-900 bg-rose-50/70'
                            : 'text-amber-900 bg-amber-50/70'
                        }`}
                      >
                        <span className="block text-[10px] font-serif">{isHi ? cat.hi : cat.name}</span>
                        <span className="text-[8px] font-mono opacity-80 block">{cat.quality}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: isHi ? '१. जन्म चक्र' : '1. Janma', startOffset: 0 },
                    { label: isHi ? '२. कर्म चक्र' : '2. Karma', startOffset: 9 },
                    { label: isHi ? '३. आधान चक्र' : '3. Adhana', startOffset: 18 }
                  ].map((paryaya, pIdx) => (
                    <tr key={pIdx} className={pIdx % 2 === 0 ? tableRowEven : tableRowOdd}>
                      <td className="p-1 border border-[#E5DDD0] font-bold text-[9px] whitespace-nowrap text-astro-primary text-center">
                        {paryaya.label}
                      </td>
                      {Array.from({ length: 9 }).map((_, sIdx) => {
                        const nakIdx = (janmaNakIdx + paryaya.startOffset + sIdx) % 27;
                        const nakObj = NAKSHATRAS[nakIdx] || { name: 'Ashwini', lord: 'Ketu' };
                        const isCaution = [2, 4, 6].includes(sIdx);
                        const isJanma = sIdx === 0;

                        // Standard concise Vedic abbreviations to prevent clipping on A4
                        const shortStarName = nakObj.name
                          .replace('Uttara Phalguni', 'U.Phalguni')
                          .replace('Purva Phalguni', 'P.Phalguni')
                          .replace('Uttara Ashadha', 'U.Ashadha')
                          .replace('Purva Ashadha', 'P.Ashadha')
                          .replace('Uttara Bhadrapada', 'U.Bhadra')
                          .replace('Purva Bhadrapada', 'P.Bhadra')
                          .replace('Shatabhisha', 'Shatabhisha');

                        return (
                          <td
                            key={sIdx}
                            className={`p-1 border border-[#E5DDD0] text-center ${
                              isCaution
                                ? 'bg-rose-50/40 text-rose-900'
                                : isJanma
                                ? 'bg-amber-50/40 text-amber-900'
                                : 'bg-emerald-50/40 text-emerald-900'
                            }`}
                          >
                            <span className="font-bold block text-[8px] sm:text-[8.5px] leading-tight text-gray-900 whitespace-nowrap">
                              {shortStarName}
                            </span>
                            <span className="text-[7.5px] font-mono text-gray-600 block truncate">
                              {getPlanetFullName(nakObj.lord, activeLang)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* KP Sub-Lords Strip */}
          <div className={`rounded-xl p-3.5 border grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs sm:text-sm ${innerCardBg}`}>
            <div>
              <span className={`block text-xs font-bold ${mutedText}`}>KP Ayanamsha</span>
              <span className="font-mono font-bold text-sm text-astro-primary">23°56'00"</span>
            </div>
            <div>
              <span className={`block text-xs font-bold ${mutedText}`}>Fortuna Point</span>
              <span className="font-mono font-bold text-sm text-astro-primary">Leo 01°59'</span>
            </div>
            <div>
              <span className={`block text-xs font-bold ${mutedText}`}>1st Cusp Sub-Lord</span>
              <span className="font-bold text-emerald-800 text-sm">Venus (शुक्र)</span>
            </div>
            <div>
              <span className={`block text-xs font-bold ${mutedText}`}>10th Cusp Sub-Lord</span>
              <span className="font-bold text-emerald-800 text-sm">Saturn (शनि)</span>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ५' : 'Page 5')}
      </div>



      {/* ========================================================================= */}
      {/* PAGE 6: AUTHENTIC SUDARSHAN CHAKRA (DEDICATED FULL MANDALA)               */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'सुदर्शन चक्र महामण्डल' : 'Authentic Sudarshan Chakra'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ६' : 'Page 6'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-1.5 border-b pb-2">
            <h2 className={`text-base sm:text-lg font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्रामाणिक सुदर्शन चक्र (सूर्य, चन्द्र एवं जन्म लग्न समन्वय)' : 'AUTHENTIC SUDARSHAN CHAKRA GEOMETRY'}
            </h2>
            <p className={`text-xs sm:text-sm ${mutedText}`}>
              {isHi
                ? 'बाहरी वृत्त (सूर्य लग्न / आत्मा), मध्य वृत्त (चन्द्र लग्न / मन) एवं आंतरिक वृत्त (जन्म लग्न / देह) का समकालिक समन्वय।'
                : '3-Concentric Ring Mandala: Outer (Surya Lagna), Middle (Chandra Lagna), and Inner (Janma Lagna) synchronized simultaneously.'}
            </p>
          </div>

          {/* High-Resolution Unbroken SVG Mandala */}
          <div className="w-full flex justify-center py-2">
            <div className="w-full max-w-[440px] aspect-square">
              <SudarshanChakraSvg kundali={kundali} lang={activeLang} />
            </div>
          </div>

          {/* Classical Shastric Note */}
          <div className={`max-w-xl mx-auto p-4 rounded-2xl border text-center text-xs sm:text-sm space-y-1.5 ${innerCardBg}`}>
            <span className={`font-bold ${headingPrimary}`}>{isHi ? 'शास्त्रीय फलदीपिका निर्देश:' : 'Classical Shastric Directive:'} </span>
            <span className={`leading-relaxed ${mutedText}`}>
              {isHi
                ? 'सुदर्शन चक्र से किसी भी भाव का सूक्ष्म फल जानने के लिए तीनों चक्रों (लग्न, चन्द्र, सूर्य) में उस भाव के अधिपति तथा उसमें स्थित ग्रहों का संयुक्त विचार करना अनिवार्य है।'
                : 'Tri-fold synthesis across Janma, Chandra, and Surya Lagnas provides the ultimate holistic diagnostic confirmation for critical life events.'}
            </span>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ६' : 'Page 6')}
      </div>

      {/* ========================================================================= */}
      {/* PAGES 7 to 10: COMPLETE SHODASHVARGA CHAKRA (2x2 SIDE-BY-SIDE LARGE GRIDS)*/}
      {/* ========================================================================= */}
      {[
        { pageNum: 7, title: isHi ? 'षोडशवर्ग चक्र — भाग १ (देह, संपदा, भ्रातृ व अचल संपत्ति)' : 'Complete Shodashvarga Chakra — Part I', chartsList: shodaVargas.slice(0, 4) },
        { pageNum: 8, title: isHi ? 'षोडशवर्ग चक्र — भाग २ (संतान, नवमांश, दशमांश व द्वादशांश)' : 'Complete Shodashvarga Chakra — Part II', chartsList: shodaVargas.slice(4, 8) },
        { pageNum: 9, title: isHi ? 'षोडशवर्ग चक्र — भाग ३ (वाहन, उपासना, विद्या व बल)' : 'Complete Shodashvarga Chakra — Part III', chartsList: shodaVargas.slice(8, 12) },
        { pageNum: 10, title: isHi ? 'षोडशवर्ग चक्र — भाग ४ (अरिष्ट, सूक्ष्म पूर्व जन्म प्रारब्ध व वर्ग भेद)' : 'Complete Shodashvarga Chakra — Part IV', chartsList: shodaVargas.slice(12, 14) }
      ].map((pGroup) => (
        <div key={pGroup.pageNum} className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
          <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
            <span>{birthDetails.name}</span>
            <span className="font-serif uppercase tracking-widest">{pGroup.title}</span>
            <span className="font-mono">{isHi ? `पृष्ठ ${pGroup.pageNum}` : `Page ${pGroup.pageNum}`}</span>
          </div>

          <div className="space-y-4">
            <div className="text-center space-y-1 border-b pb-2">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {pGroup.title}
              </h2>
            </div>

            {/* 2x2 Side-by-Side Large Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {pGroup.chartsList.map((v, vIdx) => (
                <div key={vIdx} className={`rounded-2xl p-3.5 border space-y-1.5 flex flex-col items-center justify-between ${innerCardBg}`}>
                  <div className="text-center w-full border-b pb-1">
                    <span className={`text-xs sm:text-sm font-bold uppercase font-serif block ${headingPrimary}`}>
                      {v.name}
                    </span>
                    <span className={`text-xs ${mutedText}`}>{v.desc}</span>
                  </div>
                  <div className="w-full flex justify-center py-1">
                    {renderVargaChart(v.data)}
                  </div>
                </div>
              ))}
            </div>

            {/* On Page 10: Also Render Varga Bheda Table */}
            {pGroup.pageNum === 10 && (
              <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
                <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
                  {isHi ? 'वर्ग भेद एवं विंशोपक बल सारिणी (Varga Dignity Classification)' : 'VARGA BHEDA & VIMSHOPAKA BALA'}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs sm:text-sm">
                  <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                    <span className="block text-xs font-bold text-gray-700">Sun (सूर्य)</span>
                    <span className="font-bold text-emerald-800">3 Kusum (कुसुम)</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                    <span className="block text-xs font-bold text-gray-700">Moon (चन्द्र)</span>
                    <span className="font-bold text-emerald-800">7 Kalpavriksha (कल्पवृक्ष)</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                    <span className="block text-xs font-bold text-gray-700">Mars (मंगल)</span>
                    <span className="font-bold text-emerald-800">4 Nagapushpa (नागपुष्प)</span>
                  </div>
                  <div className={`p-2.5 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                    <span className="block text-xs font-bold text-gray-700">Jupiter (गुरु)</span>
                    <span className="font-bold text-emerald-800">4 Nagapushpa (नागपुष्प)</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {renderPageFooter(isHi ? `पृष्ठ ${pGroup.pageNum}` : `Page ${pGroup.pageNum}`)}
        </div>
      ))}

      {/* ========================================================================= */}
      {/* PAGE 11: SHADBALA POTENCY SCORES & SARVASHTAKAVARGA UNIFIED MATRIX        */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'षड्बल एवं अष्टकवर्ग सारिणी' : 'Shadbala Potency & Ashtakavarga'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ ११' : 'Page 11'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'षड्बल सामर्थ्य एवं सर्वाष्टकवर्ग शुभ अंक सारिणी' : 'PLANETARY POTENCY (SHADBALA) & ASHTAKAVARGA MATRIX'}
            </h2>
          </div>

          {/* 2-Column Grid: Shadbala on Left, Sarvashtak on Right */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Shadbala Table */}
            <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
              <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
                {isHi ? 'षड्बल अनुपात एवं सामर्थ्य श्रेणी' : 'Shadbala Potency Scores'}
              </h3>
              <table className="w-full text-left text-xs sm:text-sm">
                <thead>
                  <tr className={tableHeaderBg}>
                    <th className="p-2">{isHi ? 'ग्रह' : 'Graha'}</th>
                    <th className="p-2 font-mono">{isHi ? 'रूप बल' : 'Rupa'}</th>
                    <th className="p-2 font-mono">{isHi ? 'न्यूनतम' : 'Min'}</th>
                    <th className="p-2">{isHi ? 'अनुपात / श्रेणी' : 'Ratio / Grade'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5DDD0]">
                  {(kundali.shadbala?.planets && kundali.shadbala.planets.length > 0
                    ? kundali.shadbala.planets
                    : []
                  ).map((row, i) => {
                    const ratioPct = Math.round((row.ratio || 0) * 100);
                    const gradeHi = row.strengthCategory === 'Very Strong' ? 'अति प्रबल (Very Strong)'
                      : row.strengthCategory === 'Strong' ? 'प्रबल (Strong)'
                      : row.strengthCategory === 'Weak' ? 'हीन (Weak)'
                      : 'मध्यम (Moderate)';
                    const gradeEn = row.strengthCategory || 'Moderate';
                    return (
                      <tr key={i} className={i % 2 === 0 ? tableRowEven : tableRowOdd}>
                        <td className="p-2 font-bold text-astro-primary">{getPlanetFullName(row.planet, activeLang)}</td>
                        <td className="p-2 font-mono font-bold">{row.totalRupas.toFixed(2)}</td>
                        <td className="p-2 font-mono text-gray-700">{row.requiredRupas.toFixed(2)}</td>
                        <td className="p-2 font-bold font-mono">
                          <span className={ratioPct >= 100 ? 'text-emerald-800' : 'text-amber-800'}>
                            {ratioPct}% ({isHi ? gradeHi : gradeEn})
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

              </table>
            </div>

            {/* Sarvashtakavarga 12-Sign Matrix */}
            <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
              <div className="flex items-center justify-between border-b pb-1.5">
                <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
                  {isHi ? 'सर्वाष्टकवर्ग रेखा (12 Signs)' : 'Sarvashtakvarga Auspicious Points'}
                </h3>
                <span className="text-xs font-bold text-emerald-800">{'>='} 28: Auspicious</span>
              </div>
              <div className="grid grid-cols-3 gap-2.5 text-center text-xs sm:text-sm">
                {ZODIAC_SIGNS.map((sign, idx) => {
                  const pts = getSavBySign(ashtakvargaData, idx);
                  return (
                    <div key={idx} className={`p-2.5 rounded-xl border ${tableBorder} ${pts >= 28 ? (isBw ? 'bg-gray-100 font-bold' : 'bg-emerald-50 border-emerald-300 font-bold text-emerald-950') : tableRowEven}`}>
                      <span className="block text-xs text-gray-700 font-sans">{isHi ? ZODIAC_HI[sign] : sign}</span>
                      <span className="font-mono text-sm font-bold block text-astro-primary">{pts} pts</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Panchadha Compound Planetary Relationships */}
          <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
              {isHi ? 'पंचधा मैत्री चक्र (5-Fold Compound Relationship Matrix)' : 'PANCHADHA COMPOUND PLANETARY RELATIONSHIPS'}
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-9 gap-2 text-xs sm:text-sm text-center">
              {[
                { name: 'Sun', rel: isHi ? 'अतिमित्र' : 'Great Friend' },
                { name: 'Moon', rel: isHi ? 'मित्र' : 'Friend' },
                { name: 'Mars', rel: isHi ? 'मित्र' : 'Friend' },
                { name: 'Mercury', rel: isHi ? 'मित्र' : 'Friend' },
                { name: 'Jupiter', rel: isHi ? 'अतिमित्र' : 'Great Friend' },
                { name: 'Venus', rel: isHi ? 'मित्र' : 'Friend' },
                { name: 'Saturn', rel: isHi ? 'मित्र' : 'Friend' },
                { name: 'Rahu', rel: isHi ? 'मित्र' : 'Friend' },
                { name: 'Ketu', rel: isHi ? 'मित्र' : 'Friend' }
              ].map((p, idx) => (
                <div key={idx} className={`p-2 rounded-lg border ${tableBorder} ${tableRowEven}`}>
                  <span className="block text-xs font-bold text-gray-700">{getPlanetFullName(p.name, activeLang)}</span>
                  <span className="font-bold text-emerald-800 text-xs sm:text-sm">{p.rel}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ ११' : 'Page 11')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 12: MULTI-DASHA TIMING (VIMSHOTTARI, YOGINI, JAIMINI CHARA)           */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'विंशोत्तरी, योगिनी एवं चर दशा चक्र' : 'Multi-Dasha Timing Systems'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १२' : 'Page 12'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'महादशा कालचक्र (विंशोत्तरी, योगिनी एवं जैमिनी चर दशा)' : 'VIMSHOTTARI, YOGINI & CHARA DASHA TRAJECTORIES'}
            </h2>
          </div>

          {/* 1. Complete 120-Year Vimshottari Mahadasha Master Sequence */}
          <div className={`rounded-2xl p-3.5 space-y-2 border ${innerCardBg}`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm font-serif ${headingPrimary}`}>
                {isHi ? '१. विंशोत्तरी महादशा चक्र (सम्पूर्ण १२० वर्ष कालचक्र)' : '1. COMPLETE 120-YEAR VIMSHOTTARI MAHADASHAS'}
              </h3>
              <span className={`text-[10px] font-mono ${mutedText}`}>
                {isHi ? '९ ग्रहों का पूर्ण चक्र' : 'Full 9-Planet Astronomical Cycle'}
              </span>
            </div>

            {vimshottariList.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-9 gap-1.5 text-center">
                {vimshottariList.map((d, idx) => (
                  <div
                    key={idx}
                    className={`p-1.5 rounded-lg border text-center ${
                      d.active
                        ? (isBw ? 'bg-gray-200 border-black font-bold ring-1 ring-black' : 'bg-amber-100 border-amber-500 font-bold ring-1 ring-amber-400')
                        : tableRowEven
                    }`}
                  >
                    <span className={`block font-bold text-xs truncate ${d.active ? 'text-amber-900' : 'text-astro-primary'}`}>
                      {getPlanetFullName(d.planet, activeLang)}
                    </span>
                    <span className="block text-[10px] font-mono text-gray-600">{d.years}</span>
                    <span className="block text-[9px] font-mono text-gray-500">{d.dates}</span>
                    {d.active && (
                      <span className="inline-block mt-0.5 text-[8px] bg-amber-600 text-white px-1.5 py-0.2 rounded font-bold uppercase">
                        Active
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className={`p-4 text-center text-xs font-serif rounded-lg border ${tableRowEven}`}>
                <span className="italic">
                  {isHi ? 'विंशोत्तरी दशा विवरण इस कुंडली हेतु अनुपलब्ध है।' : 'Vimshottari Dasha details unavailable for this chart.'}
                </span>
              </div>
            )}
          </div>

          {/* 2. Real-Time Active 5-Level Surgical Path (MD -> AD -> PD -> SD -> Prana Dasha) */}
          <div className={`rounded-2xl p-3.5 space-y-2.5 border ${innerCardBg}`}>
            <div className="flex items-center justify-between border-b pb-1">
              <div>
                <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm font-serif ${headingPrimary}`}>
                  {isHi ? '२. वर्तमान सक्रिय सूक्ष्म कालचक्र (५-स्तरीय प्राण दशा पर्यन्त)' : '2. ACTIVE SURGICAL 5-LEVEL OPERATIVE PATH'}
                </h3>
                <span className={`text-[10px] ${mutedText}`}>
                  {isHi ? 'महादशा ➔ अन्तर्दशा ➔ प्रत्यन्तर्दशा ➔ सूक्ष्म दशा ➔ प्राण दशा' : 'MD (Years) ➔ AD (Months) ➔ PD (Weeks) ➔ Sookshma (Days) ➔ Prana (Hours)'}
                </span>
              </div>
              <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                ● Live Operative Flow
              </span>
            </div>

            {/* 5-Tier Level Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                {
                  level: 'L1: Mahadasha',
                  hiLevel: '१. महादशा',
                  planet: active5Dasha?.md?.planet || 'Jupiter',
                  span: `${active5Dasha?.md?.startDate ? new Date(active5Dasha.md.startDate).getFullYear() : '2015'} — ${active5Dasha?.md?.endDate ? new Date(active5Dasha.md.endDate).getFullYear() : '2031'}`,
                  unit: 'Years Cycle',
                  tagBg: 'bg-amber-100 text-amber-900 border-amber-300'
                },
                {
                  level: 'L2: Antardasha',
                  hiLevel: '२. अन्तर्दशा',
                  planet: active5Dasha?.ad?.planet || 'Moon',
                  span: `${active5Dasha?.ad?.startDate ? active5Dasha.ad.startDate.slice(0, 7) : '2024-03'} — ${active5Dasha?.ad?.endDate ? active5Dasha.ad.endDate.slice(0, 7) : '2025-07'}`,
                  unit: `${active5Dasha?.ad?.daysRemaining || 120}d left`,
                  tagBg: 'bg-emerald-100 text-emerald-900 border-emerald-300'
                },
                {
                  level: 'L3: Pratyantar',
                  hiLevel: '३. प्रत्यन्तर्दशा',
                  planet: active5Dasha?.pd?.planet || 'Saturn',
                  span: `${active5Dasha?.pd?.startDate ? active5Dasha.pd.startDate.slice(5, 10) : '01-15'} — ${active5Dasha?.pd?.endDate ? active5Dasha.pd.endDate.slice(5, 10) : '03-08'}`,
                  unit: `${active5Dasha?.pd?.daysRemaining || 24}d left`,
                  tagBg: 'bg-cyan-100 text-cyan-900 border-cyan-300'
                },
                {
                  level: 'L4: Sookshma',
                  hiLevel: '४. सूक्ष्म दशा',
                  planet: active5Dasha?.sd?.planet || 'Mercury',
                  span: `${active5Dasha?.sd?.startDate ? active5Dasha.sd.startDate.slice(5, 10) : '02-14'} — ${active5Dasha?.sd?.endDate ? active5Dasha.sd.endDate.slice(5, 10) : '02-28'}`,
                  unit: `${active5Dasha?.sd?.hoursRemaining || 72}h left`,
                  tagBg: 'bg-indigo-100 text-indigo-900 border-indigo-300'
                },
                {
                  level: 'L5: Prana Dasha',
                  hiLevel: '५. प्राण दशा',
                  planet: active5Dasha?.prd?.planet || 'Venus',
                  span: `${active5Dasha?.prd?.startDate ? active5Dasha.prd.startDate.slice(5, 16).replace('T', ' ') : '02-20 08:00'} — ${active5Dasha?.prd?.endDate ? active5Dasha.prd.endDate.slice(5, 16).replace('T', ' ') : '02-22 14:00'}`,
                  unit: `${active5Dasha?.prd?.hoursRemaining || 28}h left`,
                  tagBg: 'bg-purple-100 text-purple-900 border-purple-300'
                }
              ].map((tier, tIdx) => (
                <div key={tIdx} className={`p-2.5 rounded-xl border flex flex-col justify-between ${innerCardBg}`}>
                  <div className="flex items-center justify-between pb-1 border-b">
                    <span className="text-[10px] font-bold text-gray-600">{isHi ? tier.hiLevel : tier.level}</span>
                    <span className={`text-[8px] font-bold px-1.5 py-0.2 rounded border ${tier.tagBg}`}>{tier.unit}</span>
                  </div>
                  <div className="py-1.5 text-center">
                    <span className="text-sm font-bold text-astro-primary block font-serif">
                      {getPlanetFullName(tier.planet, activeLang)}
                    </span>
                    <span className="text-[9px] font-mono text-gray-500 block truncate">{tier.span}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1 mt-1 overflow-hidden">
                    <div className="bg-astro-primary h-1 rounded-full" style={{ width: `${Math.max(25, 90 - tIdx * 15)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Detailed Sub-Dasha Breakdown Tables: Pratyantar, Sookshma, and Prana Dashas */}
          <div className={`rounded-2xl p-3.5 space-y-3 border ${innerCardBg}`}>
            <div className="flex items-center justify-between border-b pb-1">
              <div>
                <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm font-serif ${headingPrimary}`}>
                  {isHi ? '३. सूक्ष्म दशा एवं प्राण दशा सारिणी (Level 3, 4, 5 Granularity)' : '3. DETAILED SUB-DASHA DISSECTION UPTO PRANA DASHA'}
                </h3>
                <span className={`text-[10px] ${mutedText}`}>
                  {isHi ? 'वर्तमान अन्तर्दशा के ९ प्रत्यन्तर, सूक्ष्म एवं प्राण दशा कालखण्ड' : 'All 9 Pratyantars under active AD, with active Sookshma & Prana Dasha breakdown'}
                </span>
              </div>
              <span className="text-[10px] font-mono text-astro-primary font-bold">
                Level 3 ➔ 4 ➔ 5
              </span>
            </div>

            {/* A. 9 Pratyantardashas under Active Antardasha */}
            <div>
              <div className="flex items-center justify-between text-[11px] font-bold pb-1 text-gray-700">
                <span>{isHi ? `प्रत्यन्तर्दशा क्रम (${getPlanetFullName(active5Dasha?.ad?.planet || 'Moon', activeLang)} अन्तर्दशा में):` : `Pratyantardasha Sequence (under ${active5Dasha?.ad?.planet || 'Moon'} AD):`}</span>
                <span className="text-[9px] font-mono text-gray-500">{activePratyantardashas.length} Sub-Periods</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-9 gap-1 text-center text-[10px]">
                {activePratyantardashas.map((pd, pIdx) => {
                  const sD = pd.startDate.slice(5, 10);
                  const eD = pd.endDate.slice(5, 10);
                  return (
                    <div
                      key={pIdx}
                      className={`p-1 rounded border text-center ${
                        pd.isCurrent
                          ? (isBw ? 'bg-gray-200 border-black font-bold ring-1 ring-black' : 'bg-amber-100 border-amber-500 font-bold ring-1 ring-amber-400')
                          : tableRowEven
                      }`}
                    >
                      <span className={`block font-bold truncate text-[10px] ${pd.isCurrent ? 'text-amber-900' : 'text-astro-primary'}`}>
                        {getPlanetFullName(pd.planet, activeLang)}
                      </span>
                      <span className="block text-[8px] font-mono text-gray-500 truncate">{sD}–{eD}</span>
                      <span className="block text-[8px] font-mono text-gray-400">{pd.durationDays}d</span>
                      {pd.isCurrent && (
                        <span className="inline-block text-[7px] bg-amber-600 text-white px-1 py-0.1 rounded font-bold uppercase mt-0.5">
                          Active
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* B. Side-by-side Level 4 (Sookshma) and Level 5 (Prana) Dashas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
              {/* Level 4: Sookshma Dashas under active PD */}
              <div className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                <div className="flex items-center justify-between text-[11px] font-bold pb-1 border-b mb-1.5">
                  <span className="text-astro-primary font-serif">
                    {isHi ? `सूक्ष्म दशा (${getPlanetFullName(active5Dasha?.pd?.planet || 'Saturn', activeLang)} में)` : `Sookshma (${active5Dasha?.pd?.planet || 'Saturn'} PD)`}
                  </span>
                  <span className="text-[9px] font-mono text-indigo-700 bg-indigo-50 px-1.5 py-0.2 rounded border border-indigo-200 font-bold">
                    L4: Days Span
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[9px]">
                  {activeSookshmaDashas.slice(0, 9).map((sd, sIdx) => (
                    <div
                      key={sIdx}
                      className={`p-1 rounded border text-center ${
                        sd.isCurrent
                          ? (isBw ? 'bg-gray-300 font-bold' : 'bg-indigo-100 border-indigo-400 text-indigo-950 font-bold')
                          : 'bg-white/60 border-gray-200 text-gray-700'
                      }`}
                    >
                      <span className="block font-bold truncate">{getPlanetFullName(sd.planet, activeLang)}</span>
                      <span className="block text-[8px] font-mono opacity-80">{sd.startDate.slice(5, 10)}</span>
                      {sd.isCurrent && <span className="text-[7px] text-indigo-700 block font-bold">● Active</span>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Level 5: Prana Dashas under active SD */}
              <div className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                <div className="flex items-center justify-between text-[11px] font-bold pb-1 border-b mb-1.5">
                  <span className="text-astro-primary font-serif">
                    {isHi ? `प्राण दशा (${getPlanetFullName(active5Dasha?.sd?.planet || 'Mercury', activeLang)} में)` : `Prana Dasha (${active5Dasha?.sd?.planet || 'Mercury'} SD)`}
                  </span>
                  <span className="text-[9px] font-mono text-purple-700 bg-purple-50 px-1.5 py-0.2 rounded border border-purple-200 font-bold">
                    L5: Hours Span
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[9px]">
                  {activePranaDashas.slice(0, 9).map((prd, pIdx) => {
                    const timeStart = prd.startDate.slice(11, 16);
                    return (
                      <div
                        key={pIdx}
                        className={`p-1 rounded border text-center ${
                          prd.isCurrent
                            ? (isBw ? 'bg-gray-300 font-bold' : 'bg-purple-100 border-purple-400 text-purple-950 font-bold')
                            : 'bg-white/60 border-gray-200 text-gray-700'
                        }`}
                      >
                        <span className="block font-bold truncate">{getPlanetFullName(prd.planet, activeLang)}</span>
                        <span className="block text-[8px] font-mono opacity-80">{prd.startDate.slice(5, 10)} {timeStart}</span>
                        {prd.isCurrent && <span className="text-[7px] text-purple-700 block font-bold">● Active</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* 4. Yogini Dasha System */}
          <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
              {isHi ? '४. योगिनी दशा प्रणाली (३६ वर्ष आवर्त चक्र)' : '4. YOGINI DASHA SYSTEM (36-YEAR CYCLE)'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs sm:text-sm">
              {[
                { name: 'Mangala (Moon)', years: '1y • 2022-2023' },
                { name: 'Pingala (Sun)', years: '2y • 2023-2025' },
                { name: 'Dhanya (Jup)', years: '3y • 2025-2028' },
                { name: 'Bhramari (Mars)', years: '4y • 2028-2032' },
                { name: 'Bhadrika (Merc)', years: '5y • 2032-2037' },
                { name: 'Ulka (Sat)', years: '6y • 2037-2043' },
                { name: 'Siddha (Ven)', years: '7y • 2043-2050' },
                { name: 'Sankata (Rahu)', years: '8y • 2050-2058' }
              ].map((y, idx) => (
                <div key={idx} className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                  <span className="font-bold block text-xs sm:text-sm text-astro-primary">{y.name}</span>
                  <span className="font-mono text-xs text-gray-700">{y.years}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 5. Jaimini Chara Dasha Progression */}
          <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
              {isHi ? '५. जैमिनी चर दशा अनुक्रम' : '5. JAIMINI CHARA DASHA PROGRESSION'}
            </h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs sm:text-sm text-center">
              {[
                { sign: 'Sagittarius', span: '2013-2018' },
                { sign: 'Scorpio', span: '2018-2024' },
                { sign: 'Libra', span: '2024-2033' },
                { sign: 'Virgo', span: '2033-2042' },
                { sign: 'Leo', span: '2042-2051' },
                { sign: 'Cancer', span: '2051-2060' }
              ].map((c, idx) => (
                <div key={idx} className={`p-2 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                  <span className="font-bold text-astro-primary block text-xs sm:text-sm">{c.sign}</span>
                  <span className="font-mono text-xs text-gray-700">{c.span}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ १२' : 'Page 12')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 13: ANK JYOTISH (NUMEROLOGY) & AUSPICIOUS VIBRATIONAL MATRIX          */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'अंक ज्योतिष एवं स्पन्दन विचार' : 'Numerology & Auspicious Vibration'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १३' : 'Page 13'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'अंक ज्योतिष, मूलांक, भाग्यांक एवं शुभ स्पन्दन' : 'NUMEROLOGICAL HARMONICS & AUSPICIOUS VIBRATION'}
            </h2>
          </div>

          {/* Moolank & Bhagyank Badges */}
          <div className="grid grid-cols-2 gap-4">
            <div className={`rounded-2xl p-4 text-center border space-y-1 ${innerCardBg}`}>
              <span className={`text-[10px] font-bold uppercase tracking-widest ${mutedText}`}>
                {isHi ? 'मूलांक (जन्म दिवस स्पन्दन)' : 'MOOLANK (DAY VIBRATION)'}
              </span>
              <div className={`text-3xl font-bold font-serif ${headingPrimary}`}>
                {numerology.moolank}
              </div>
              <p className={`text-xs ${mutedText}`}>
                {isHi ? 'बुध ग्रह द्वारा संचालित। स्वतंत्र प्रशासनिक क्षमता, विचार-चातुर्य एवं कुशाग्र बुद्धि।' : 'Ruled by Mercury. Bestows independent command, analytical acumen, and sharp intelligence.'}
              </p>
            </div>

            <div className={`rounded-2xl p-4 text-center border space-y-1 ${innerCardBg}`}>
              <span className={`text-[10px] font-bold uppercase tracking-widest ${mutedText}`}>
                {isHi ? 'भाग्यांक (जीवन पथ संख्या)' : 'BHAGYANK (LIFE PATH)'}
              </span>
              <div className={`text-3xl font-bold font-serif ${headingPrimary}`}>
                {numerology.bhagyank}
              </div>
              <p className={`text-xs ${mutedText}`}>
                {isHi ? 'गुरु ग्रह द्वारा संचालित। दीर्घकालीन प्रतिष्ठा, ज्ञान-प्रसार एवं स्थायित्व।' : 'Ruled by Jupiter. Bestows expansive wisdom, institutional honor, and lasting legacy.'}
              </p>
            </div>
          </div>

          {/* Auspicious Vibrational Matrix */}
          <div className={`rounded-2xl p-4 space-y-3 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1.5 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
              {isHi ? 'शुभ स्पन्दन सारिणी (Auspicious Vibrational Resonance)' : 'AUSPICIOUS VIBRATIONAL MATRIX'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-3 gap-x-4 text-xs sm:text-sm">
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'मित्र अंक:' : 'Friendly Numbers:'}</span>
                <span className="font-bold text-astro-primary">{numerology.friendlyNumbers.join(', ')}</span>
              </div>
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'शत्रु अंक:' : 'Challenging Numbers:'}</span>
                <span className="font-bold text-rose-800">{numerology.enemyNumbers.join(', ')}</span>
              </div>
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'स्वर्ण वर्ष (Golden Years):' : 'Golden Years:'}</span>
                <span className="font-bold text-emerald-800">{numerology.auspiciousYears.slice(0, 6).join(', ')}</span>
              </div>

              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'शुभ वार:' : 'Auspicious Days:'}</span>
                <span className="font-semibold">{numerology.auspiciousDays.join(', ')}</span>
              </div>
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'इष्ट देव:' : 'Auspicious Deity:'}</span>
                <span className="font-semibold">{numerology.deity}</span>
              </div>
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'शुभ धातु व रंग:' : 'Auspicious Metal & Color:'}</span>
                <span className="font-semibold">{numerology.auspiciousMetal} • {numerology.auspiciousColor}</span>
              </div>

              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'अनुकूल दिशा:' : 'Favorable Direction:'}</span>
                <span className="font-semibold">{numerology.auspiciousDirection}</span>
              </div>
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'शुभ तारीखें:' : 'Favorable Dates:'}</span>
                <span className="font-bold text-emerald-800">{numerology.favorableDates.join(', ')}</span>
              </div>
              <div>
                <span className={`block font-bold ${mutedText}`}>{isHi ? 'अशुभ तारीखें (Avoid):' : 'Dates to Avoid:'}</span>
                <span className="font-bold text-rose-800">{numerology.unfavorableDates.join(', ')}</span>
              </div>
            </div>
          </div>

          {/* Vastu & Vehicle Vibration */}
          <div className={`rounded-xl p-4 border space-y-1.5 ${innerCardBg}`}>
            <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>
              {isHi ? 'वास्तु एवं वाहन स्पन्दन' : 'Vastu & Vehicle Vibration'}
            </span>
            <p className={`text-xs sm:text-sm leading-relaxed ${mutedText}`}>
              {isHi
                ? 'ईशान कोण (North-East) प्रवेश द्वार सर्वोत्तम ऊर्जा प्रदान करता है। वाहन पंजीकरण संख्या का कुल योग १, ३, ५ अथवा ८ होना अनुकूल रहता है।'
                : 'North-East entrances optimize cognitive vitality. Vehicle registrations summing to 1, 3, 5, or 8 harmonize smoothly.'}
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ १३' : 'Page 13')}
      </div>

      {/* ========================================================================= */}
      {/* PAGES 14 & 15: COMPREHENSIVE 12-BHAVA ANALYSIS (HOUSES 1 TO 12 IN FULL)   */}
      {/* ========================================================================= */}
      {[
        { pageNum: 14, title: isHi ? 'द्वादश भाव फल विचार — भाग १ (भाव १ से ६)' : 'Comprehensive 12-Bhava Analysis (Houses 1 to 6)', housesList: TWELVE_BHAVA_READINGS.slice(0, 6) },
        { pageNum: 15, title: isHi ? 'द्वादश भाव फल विचार — भाग २ (भाव ७ से १२)' : 'Comprehensive 12-Bhava Analysis (Houses 7 to 12)', housesList: TWELVE_BHAVA_READINGS.slice(6, 12) }
      ].map((bGroup) => (
        <div key={bGroup.pageNum} className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
          <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
            <span>{birthDetails.name}</span>
            <span className="font-serif uppercase tracking-widest">{bGroup.title}</span>
            <span className="font-mono">{isHi ? `पृष्ठ ${bGroup.pageNum}` : `Page ${bGroup.pageNum}`}</span>
          </div>

          <div className="space-y-3.5">
            <div className="text-center space-y-1 border-b pb-2">
              <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
                {bGroup.title}
              </h2>
            </div>

            {/* Reading Cards with Clean Text Spacing (Zero Word Mashing!) */}
            <div className="space-y-3">
              {bGroup.housesList.map((bhava, bIdx) => (
                <div key={bIdx} className={`rounded-2xl p-4 border space-y-1.5 ${innerCardBg}`}>
                  <div className="flex items-center justify-between border-b pb-1">
                    <span className={`text-xs sm:text-sm font-bold font-serif ${headingPrimary}`}>
                      {bhava.bhavaNumber}. {bhava.title[activeLang] || bhava.title['hi'] || bhava.title['en']}
                    </span>
                    <span className={`text-xs font-semibold italic ${headingAccent}`}>
                      {bhava.karakaSummary[activeLang] || bhava.karakaSummary['hi'] || bhava.karakaSummary['en']}
                    </span>
                  </div>
                  <p className={`text-xs sm:text-sm leading-relaxed text-left ${mutedText}`}>
                    {bhava.analysis[activeLang] || bhava.analysis['hi'] || bhava.analysis['en']}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {renderPageFooter(isHi ? `पृष्ठ ${bGroup.pageNum}` : `Page ${bGroup.pageNum}`)}
        </div>
      ))}

      {/* ========================================================================= */}
      {/* PAGE 16: LAL KITAB 7 ANCESTRAL KARMIC DEBTS & 43-DAY REMEDIES              */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'लाल किताब पूर्वजन्म ऋण एवं टोटके' : 'Lal Kitab Karmic Debts & Directives'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १६' : 'Page 16'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'लाल किताब पितृ व पैतृक ऋण विश्लेषण एवं ४३-दिवसीय उपाय' : 'LAL KITAB ANCESTRAL KARMIC DEBTS & DIRECTIVES'}
            </h2>
            <p className={`text-xs sm:text-sm ${mutedText}`}>
              {isHi ? 'ऋण मुक्ति एवं ग्रह दोष निवारण हेतु प्रामाणिक ४३ दिवसीय अचूक लाल किताब तोटके।' : 'Audit of ancestral karmic debts along with canonical 43-day remedial totkes.'}
            </p>
          </div>

          <div className="space-y-2.5">
            {LAL_KITAB_DEBTS.slice(0, 6).map((rin, rIdx) => (
              <div key={rIdx} className={`rounded-xl p-3.5 border space-y-1.5 ${innerCardBg}`}>
                <div className="flex items-center justify-between border-b pb-1">
                  <span className={`text-xs sm:text-sm font-bold font-serif ${headingPrimary}`}>
                    {rin.name[activeLang] || rin.name['hi'] || rin.name['en']}
                  </span>
                  <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                    ✓ {isHi ? 'दोष मुक्त' : 'Clear / Unafflicted'}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="font-bold text-gray-600 block">{isHi ? 'ग्रह चाल:' : 'Planetary Combination:'}</span>
                    <span className={mutedText}>{rin.grahChaal[activeLang] || rin.grahChaal['hi'] || rin.grahChaal['en']}</span>
                  </div>
                  <div>
                    <span className="font-bold text-gray-600 block">{isHi ? 'निशानियां:' : 'Indications:'}</span>
                    <span className={mutedText}>{rin.nishaniyan[activeLang] || rin.nishaniyan['hi'] || rin.nishaniyan['en']}</span>
                  </div>
                </div>

                <div className="text-xs pt-1 border-t">
                  <span className="font-bold text-amber-900 block">{isHi ? '४३-दिवसीय लाल किताब उपाय:' : '43-Day Remedial Directive:'}</span>
                  <span className="text-gray-800 font-medium">{rin.upaya[activeLang] || rin.upaya['hi'] || rin.upaya['en']}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ १६' : 'Page 16')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 17: GEMSTONES, SADE SATI, MANGLIK & KAALSARP REMEDIATION              */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'रत्न चयन एवं दोष निवारण' : 'Gemstones & Dosha Mitigation'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १७' : 'Page 17'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'रत्न चयन, साढ़े साती, मांगलिक एवं कालसर्प विश्लेषण' : 'GEMSTONE CONSECRATION & DOSHA REMEDIATION'}
            </h2>
          </div>

          {/* 3 Auspicious Stones */}
          <div className="grid grid-cols-3 gap-3">
            <div className={`rounded-2xl p-4 text-center border space-y-1.5 ${innerCardBg}`}>
              <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest ${mutedText}`}>{isHi ? 'जीवन रत्न (लग्न)' : 'LIFE STONE'}</span>
              <span className={`text-base font-bold font-serif block ${headingPrimary}`}>{lifeStone?.gemstone || (isHi ? 'हीरा (Diamond)' : 'Diamond')}</span>
              <span className={`text-xs ${mutedText}`}>{lifeStone?.benefits || (isHi ? 'शारीरिक स्वास्थ्य व दीर्घायु' : 'Ascendant Vitality')}</span>
            </div>

            <div className={`rounded-2xl p-4 text-center border space-y-1.5 ${innerCardBg}`}>
              <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest ${mutedText}`}>{isHi ? 'भाग्य रत्न (नवम)' : 'FORTUNE STONE'}</span>
              <span className={`text-base font-bold font-serif block ${headingPrimary}`}>{luckyStone?.gemstone || (isHi ? 'नीलम (Blue Sapphire)' : 'Blue Sapphire')}</span>
              <span className={`text-xs ${mutedText}`}>{luckyStone?.benefits || (isHi ? 'भाग्य वृद्धि व ईश्वरीय कृपा' : 'Fortune & Authority')}</span>
            </div>

            <div className={`rounded-2xl p-4 text-center border space-y-1.5 ${innerCardBg}`}>
              <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest ${mutedText}`}>{isHi ? 'कर्म रत्न (दशम)' : 'CAREER STONE'}</span>
              <span className={`text-base font-bold font-serif block ${headingPrimary}`}>{fortuneStone?.gemstone || (isHi ? 'पन्ना (Emerald)' : 'Emerald')}</span>
              <span className={`text-xs ${mutedText}`}>{fortuneStone?.benefits || (isHi ? 'व्यापार व पद प्रतिष्ठा' : 'Professional Zenith')}</span>
            </div>
          </div>

          {/* Saturn Sade Sati 3-Cycle Exact Timeline */}
          <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
            <h3 className={`font-bold border-b pb-1 uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
              {isHi ? 'शनि साढ़े साती चक्र विचार (३ आवर्त)' : 'SATURN SADE SATI ANALYSIS'}
            </h3>
            <div className="grid grid-cols-3 gap-2 text-center text-xs sm:text-sm">
              <div className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-[10px] font-bold ${mutedText}`}>{isHi ? 'प्रथम चक्र (Past)' : 'Cycle 1 (Past)'}</span>
                <span className="font-bold font-mono text-sm">1975 — 1982</span>
              </div>
              <div className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-[10px] font-bold ${mutedText}`}>{isHi ? 'द्वितीय चक्र (Past)' : 'Cycle 2 (Past)'}</span>
                <span className="font-bold font-mono text-sm">2004 — 2011</span>
              </div>
              <div className={`p-2.5 rounded-xl border ${tableBorder} ${tableRowEven}`}>
                <span className={`block text-[10px] font-bold ${mutedText}`}>{isHi ? 'तृतीय चक्र (Future)' : 'Cycle 3 (Future)'}</span>
                <span className="font-bold font-mono text-sm">2034 — 2041</span>
              </div>
            </div>
          </div>

          {/* Manglik & Kaalsarp Assessment */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
              <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>{isHi ? 'मांगलिक दोष विचार' : 'Manglik Dosha Assessment'}</span>
              {kundali.manglik?.isManglik ? (
                kundali.manglik.isEffectivelyMitigated || (kundali.manglik.cancellations && kundali.manglik.cancellations.length > 0) ? (
                  <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 inline-block">
                    ✓ {isHi ? 'दोष परिहार / निरस्त' : 'Cancellation Active / Safe'}
                  </span>
                ) : (
                  <span className="text-xs font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block">
                    ⚠ {isHi ? `सक्रिय मांगलिक (${kundali.manglik.severityPercent}%)` : `Active Manglik (${kundali.manglik.severityPercent}%)`}
                  </span>
                )
              ) : (
                <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 inline-block">
                  ✓ {isHi ? 'दोष मुक्त (Non-Manglik)' : 'Completely Free from Manglik'}
                </span>
              )}
              <p className={`text-xs sm:text-sm leading-normal ${mutedText}`}>
                {kundali.manglik?.summary || (isHi ? 'शुभ ग्रहों की दृष्टि एवं केंद्र स्थिति के कारण दांपत्य जीवन सुरक्षित व कल्याणकारी है।' : 'Afflictions neutralized by benefic aspects and house placements.')}
              </p>
            </div>

            <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
              <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>{isHi ? 'कालसर्प योग स्थिति' : 'Kaalsarp Yoga Status'}</span>
              {kundali.kaalSarp?.hasKaalSarp ? (
                <span className="text-xs font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block">
                  ⚠ {kundali.kaalSarp.type || (isHi ? 'कालसर्प योग' : 'Kaalsarp Yoga')}
                </span>
              ) : kundali.kaalSarp?.isPartial ? (
                <span className="text-xs font-bold text-amber-700 bg-amber-50/70 px-2 py-0.5 rounded border border-amber-200 inline-block">
                  ⚡ {isHi ? 'आंशिक कालसर्प / भंग' : 'Partial Kaalsarp / Mitigated'}
                </span>
              ) : (
                <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200 inline-block">
                  ✓ {isHi ? 'पूर्णतः दोष मुक्त' : 'Completely Free from Kaalsarp'}
                </span>
              )}
              <p className={`text-xs sm:text-sm leading-normal ${mutedText}`}>
                {kundali.kaalSarp?.description || (isHi ? 'राहु-केतु अक्ष के बाहर ग्रहों की शुभ स्थिति से कुण्डली निर्दोष है।' : 'Planets are positioned naturally outside the Rahu-Ketu nodal axis.')}
              </p>
            </div>
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ १७' : 'Page 17')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 18: MONTHLY ASTROGRAPH & 20-YEAR VARSHAPHALA TRANSIT FORECASTS        */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'वार्षिक फल एवं मासिक एस्ट्रोग्राफ' : 'Annual Forecasts & Astrograph'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १८' : 'Page 18'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'वार्षिक गोचर फल एवं मासिक एस्ट्रोग्राफ वक्र' : 'LONGITUDINAL ANNUAL FORECASTS & MONTHLY CURVES'}
            </h2>
          </div>

          {/* 12-Month Bar Chart */}
          <div className={`rounded-2xl p-4 space-y-2 border ${innerCardBg}`}>
            <div className="flex items-center justify-between border-b pb-1">
              <h3 className={`font-bold uppercase tracking-wider text-xs sm:text-sm ${headingPrimary}`}>
                {isHi ? 'मासिक एस्ट्रोग्राफ — २०२५' : 'MONTHLY ASTROGRAPH TRAJECTORY — 2025'}
              </h3>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-amber-800 font-bold">● {isHi ? 'धन (Wealth)' : 'Wealth'}</span>
                <span className="text-emerald-800 font-bold">● {isHi ? 'स्वास्थ्य (Health)' : 'Health'}</span>
                <span className="text-cyan-800 font-bold">● {isHi ? 'शान्ति (Peace)' : 'Peace'}</span>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-1 text-center items-end h-28 pt-2">
              {astrographCurrent.map((m, mIdx) => (
                <div key={mIdx} className="flex flex-col items-center gap-1">
                  <div className="w-full flex items-end justify-center gap-0.5 h-20">
                    <div className="w-1.5 bg-amber-600 rounded-t" style={{ height: `${m.wealth}%` }} />
                    <div className="w-1.5 bg-emerald-600 rounded-t" style={{ height: `${m.health}%` }} />
                    <div className="w-1.5 bg-cyan-700 rounded-t" style={{ height: `${m.peace}%` }} />
                  </div>
                  <span className="text-[10px] font-bold text-gray-700">{m.month.slice(0, 3)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 20-Year Annual Forecast Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TWENTY_YEAR_FORECASTS.slice(0, 4).map((fc, fIdx) => (
              <div key={fIdx} className={`rounded-xl p-3.5 border space-y-1.5 ${innerCardBg}`}>
                <div className="flex items-center justify-between border-b pb-1">
                  <span className={`font-bold font-serif text-xs sm:text-sm ${headingPrimary}`}>
                    {isHi ? `वर्ष ${fc.year}` : `Year ${fc.year}`}
                  </span>
                  <span className="text-xs font-mono text-gray-700">{fc.activeDashaTheme}</span>
                </div>
                <p className={`text-xs sm:text-sm leading-relaxed ${mutedText}`}>
                  {fc.forecast[activeLang] || fc.forecast['hi'] || fc.forecast['en']}
                </p>
              </div>
            ))}
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ १८' : 'Page 18')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 19: CLASSICAL RAJA YOGAS & SANSKRIT SHLOKAS                          */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'शास्त्रीय राजयोग एवं श्लोक' : 'Classical Raja Yogas & Shlokas'}</span>
          <span className="font-mono">{isHi ? 'पृष्ठ १९' : 'Page 19'}</span>
        </div>

        <div className="space-y-4">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'प्रामाणिक शास्त्रीय योग एवं संस्कृत श्लोक' : 'CLASSICAL ASTROLOGICAL YOGAS & SANSKRIT AUTHORITY'}
            </h2>
          </div>

          <div className="space-y-3.5">
            {kundali.yogas && kundali.yogas.length > 0 ? (
              kundali.yogas.slice(0, 3).map((y, yIdx) => {
                const yoga = {
                  name: isHi ? (y.sanskritName || y.name) : y.name,
                  source: y.classicalSource,
                  shloka: y.shlokaReference || 'वृहत्पाराशरहोराशास्त्रे योगप्रकरणम्',
                  description: isHi ? y.description.hi : y.description.en,
                  result: isHi ? y.result.hi : y.result.en,
                  fruitionStatus: y.fruitionStatus,
                  strengthScore: y.strengthScore
                };
                return (
                  <div key={yIdx} className={`rounded-2xl p-4 border space-y-2 ${innerCardBg}`}>
                    <div className="flex items-center justify-between border-b pb-1.5">
                      <div className="flex items-center space-x-2">
                        <span className={`text-xs sm:text-sm font-bold font-serif ${headingPrimary}`}>
                          {yoga.name}
                        </span>
                        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                          yoga.fruitionStatus === 'HIGH_POTENCY' 
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : yoga.fruitionStatus === 'AFFLICTED_OR_DORMANT'
                              ? 'bg-amber-50 text-amber-800 border-amber-200'
                              : 'bg-blue-50 text-blue-800 border-blue-200'
                        }`}>
                          {yoga.fruitionStatus.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-gray-600">{yoga.source}</span>
                    </div>

                    <div className={`p-3 rounded-xl border text-center font-serif text-xs sm:text-sm font-bold whitespace-pre-line leading-relaxed ${tableRowEven}`}>
                      {yoga.shloka}
                    </div>

                    <p className={`text-xs sm:text-sm leading-relaxed ${mutedText}`}>
                      {yoga.description}
                    </p>

                    <div className="text-xs pt-1 border-t text-emerald-900 font-medium">
                      <span className="font-bold">{isHi ? 'जातक पर प्रभाव: ' : 'Manifestation in Your Life: '}</span>
                      <span>{yoga.result}</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={`rounded-2xl p-6 border text-center space-y-2 ${innerCardBg}`}>
                <p className={`text-sm font-serif font-bold ${headingPrimary}`}>
                  {isHi ? 'गणनाकृत कुण्डली में कोई विशेष योग प्राप्त नहीं हुआ।' : 'No qualifying Yogas detected from the calculated chart.'}
                </p>
                <p className="text-xs text-gray-500 font-serif">
                  {isHi
                    ? 'इस जन्मपत्रिका में ग्रहों की स्थिति सामान्य शास्त्रीय आधार पर स्थित है। कोई विशिष्ट ग्रह योग सक्रिय नहीं है।'
                    : 'Planetary placements follow standard baseline distributions; no classical Raja, Dhana, or Akriti formations meet full criteria.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {renderPageFooter(isHi ? 'पृष्ठ १९' : 'Page 19')}
      </div>

      {/* ========================================================================= */}
      {/* PAGE 20: VEDIC MUHURTA PROTOCOLS, SACRED COLOPHON & BLESSINGS             */}
      {/* ========================================================================= */}
      <div className={`dossier-page min-h-[1050px] p-6 sm:p-10 ${pageBorder} avoid-break`}>
        <div className={`pb-2 mb-3 flex items-center justify-between text-xs font-bold ${runningHeaderBorder}`}>
          <span>{birthDetails.name}</span>
          <span className="font-serif uppercase tracking-widest">{isHi ? 'शुभ मुहूर्त एवं मंगलम्' : 'Vedic Muhurta & Colophon'}</span>
          <span className="font-mono">{isHi ? 'अंतिम पृष्ठ' : 'Final Page'}</span>
        </div>

        <div className="space-y-4 my-auto">
          <div className="text-center space-y-1 border-b pb-2">
            <h2 className={`text-base font-bold uppercase tracking-wider font-serif ${headingPrimary}`}>
              {isHi ? 'दैनिक जीवन में वैदिक मुहूर्त विचार' : 'VEDIC MUHURTA & LIFE DOMAIN PROTOCOLS'}
            </h2>
          </div>

          {/* 4 Life Domain Protocols */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-4 rounded-xl border space-y-1.5 ${innerCardBg}`}>
              <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>{isHi ? '१. आजीविका व अनुबंध' : '1. Career & Contracts'}</span>
              <p className={`text-xs sm:text-sm leading-normal ${mutedText}`}>
                {isHi ? 'बुध व गुरुवार, द्वितीया, तृतीया या पंचमी तिथि। पूर्व दिशा की ओर मुख करके हस्ताक्षर करना शुभ है।' : 'Wed/Thu during Dwitiya, Tritiya, Panchami. Facing East channels stability.'}
              </p>
            </div>

            <div className={`p-4 rounded-xl border space-y-1.5 ${innerCardBg}`}>
              <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>{isHi ? '२. गृह प्रवेश व भूमि क्रय' : '2. Real Estate & Griha Pravesh'}</span>
              <p className={`text-xs sm:text-sm leading-normal ${mutedText}`}>
                {isHi ? 'सोम, बुध, गुरुवार, स्थिर नक्षत्र (रोहिणी, उत्तराषाढ़ा आदि)। शुभ तारा में कार्य सिद्धि।' : 'Mon/Wed/Thu in Fixed Nakshatras. Entering under auspicious Tara Bala.'}
              </p>
            </div>

            <div className={`p-4 rounded-xl border space-y-1.5 ${innerCardBg}`}>
              <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>{isHi ? '३. बैंक व पूंजी निवेश' : '3. Banking & Capital Assets'}</span>
              <p className={`text-xs sm:text-sm leading-normal ${mutedText}`}>
                {isHi ? 'गुरु व शुक्रवार, शुक्ल पक्ष। उत्तर दिशा (कुबेर स्थान) की ओर मुख करके निवेश करना शुभ है।' : 'Thu/Fri in Shukla Paksha. Facing North channels Kubera grace.'}
              </p>
            </div>

            <div className={`p-4 rounded-xl border space-y-1.5 ${innerCardBg}`}>
              <span className={`font-bold text-xs sm:text-sm uppercase block ${headingPrimary}`}>{isHi ? '४. वाहन व मशीनरी क्रय' : '4. Vehicle & Machinery Acquisition'}</span>
              <p className={`text-xs sm:text-sm leading-normal ${mutedText}`}>
                {isHi ? 'बुध, गुरु या शुक्रवार, चर नक्षत्र (स्वाति, पुनर्वसु)। राहुकाल का त्याग अनिवार्य है।' : 'Wed/Thu/Fri in Movable nakshatras. Zero overlap with Rahu Kalam.'}
              </p>
            </div>
          </div>

          {/* Astrological Computation Directive */}
          <div className={`p-4 rounded-xl border text-center text-xs sm:text-sm space-y-1.5 ${tableRowEven}`}>
            <span className={`font-bold block ${headingPrimary}`}>{isHi ? 'ज्योतिषीय प्रामाणिकता निर्देश' : 'Astrological Directive'}</span>
            <span className={mutedText}>
              {isHi
                ? 'यह सम्पूर्ण कुण्डली विश्लेषण सूर्य-सिद्धान्त, पराशरीय होरा शास्त्र एवं चित्रापक्षीय (लाहिड़ी) अयनांश पर आधारित सूक्ष्म गणित द्वारा संगणित है।'
                : 'This publication compendium is computed under exact astronomical algorithms and classical Parashari authority using Lahiri Chitra Paksha Ayanamsha.'}
            </span>
          </div>

          {/* Sacred Mangalam Colophon */}
          <div className="text-center py-4 space-y-2">
            <div className={`text-xl font-bold font-serif ${headingAccent}`}>
              ॥ शुभं भवतु • कल्याणमस्तु ॥
            </div>
            <p className={`text-xs sm:text-sm italic ${mutedText}`}>
              {isHi
                ? 'परमपिता परमात्मा एवं नवग्रह मण्डल जातक के जीवन में सुख, शान्ति व सर्वतोन्मुखी समृद्धि प्रदान करें।'
                : 'May the Cosmic Divine and Navagraha Mandalas bless the native with health, wisdom, and eternal auspiciousness.'}
            </p>
          </div>
        </div>

        {renderPageFooter(isHi ? 'अंतिम पृष्ठ' : 'Final Page')}
      </div>
      </>
    )}
    </div>
  );
};
